import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  ListOrdered,
  Download,
  CalendarRange,
  TrendingUp,
  Filter,
  ShieldAlert,
  BookmarkPlus,
  CalendarClock,
  FileText,
  Trash2,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  PieChart,
  MapPin,
  Wand2,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useMenuCategoryOrdering } from "@/features/restaurant/hooks/useMenuCategoryOrdering";
import { useExportJob, type ExportDeliveryDestination } from "@/hooks/useExportJob";
import {
  useParBreachNotifications,
  type ParBreachNotificationInput,
  useKpiGuardrailNotifications,
  type KpiGuardrailNotificationInput,
} from "@/hooks/useNotifications";
import { useRestaurantSettings } from "@/features/restaurant/hooks/useRestaurantSettings";
import { buildCateringOverview } from "@/features/restaurant/utils/cateringOverview";
import {
  createReportSchedule,
  deleteReportSchedule,
  downloadFinanceSnapshot,
  getIncidentReport,
  getReports,
  listReportSchedules,
  type ReportSchedule,
  type ReportExportTarget,
  type ReportsInventoryInsights,
  type ReportsLaborInsights,
} from "@/lib/api/reports";
import { fetchAnalyticsDashboard, requestAccountingExport, type AccountingExportType } from "@/lib/api/analytics";
import { fetchEffectiveLoyaltyProgram } from "@/lib/api/loyalty";
import { useTenantStore } from "@/stores/tenantStore";
import { formatDistanceToNow } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { BarList } from "@/components/ui/charts/bar-list";
import { WaterfallChart } from "@/components/ui/charts/waterfall";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
});

const percentFormatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const quantityFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

const dayFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const buildNormalizedBars = (values: number[]): number[] => {
  if (values.length === 0) {
    return Array.from({ length: 8 }, () => 0);
  }

  const max = Math.max(...values);
  if (max <= 0) {
    return values.map(() => 0);
  }

  return values.map((value) => {
    const ratio = value / max;
    const height = Math.round(ratio * 100);
    return Math.max(8, Math.min(100, height));
  });
};

const MINUTES_PER_DAY = 24 * 60;

const normalizeMinuteValue = (input: number): number => {
  if (!Number.isFinite(input)) {
    return 0;
  }
  const normalized = input % MINUTES_PER_DAY;
  return normalized < 0 ? normalized + MINUTES_PER_DAY : normalized;
};

const formatMinuteLabel = (input: number): string => {
  const minutes = normalizeMinuteValue(input);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

const formatDeltaPercentLabel = (value: number | null): string => {
  if (value === null) {
    return "-";
  }
  if (!Number.isFinite(value)) {
    return "-";
  }
  if (value === 0) {
    return percentFormatter.format(0);
  }
  const formatted = percentFormatter.format(Math.abs(value));
  return `${value > 0 ? "+" : "−"}${formatted}`;
};

const formatSecondsAsDuration = (input: number): string => {
  if (!Number.isFinite(input) || input <= 0) {
    return "0s";
  }
  const totalSeconds = Math.max(0, Math.round(input));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${totalSeconds}s`;
  }
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
};

const determineTrend = (deltaPercent: number | null, deltaAbsolute: number): "up" | "down" | "flat" => {
  if (deltaPercent === null) {
    if (deltaAbsolute > 0) {
      return "up";
    }
    if (deltaAbsolute < 0) {
      return "down";
    }
    return "flat";
  }
  if (deltaPercent > 0.05) {
    return "up";
  }
  if (deltaPercent < -0.05) {
    return "down";
  }
  return "flat";
};

const trendStyleMap: Record<"up" | "down" | "flat", string> = {
  up: "text-emerald-600 dark:text-emerald-300",
  down: "text-destructive",
  flat: "text-muted-foreground",
};

const trendBadgeMap: Record<"up" | "down" | "flat", string> = {
  up: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  down: "bg-destructive/10 text-destructive",
  flat: "bg-muted text-muted-foreground",
};

const TableScrollContainer = ({
  children,
  className,
  minWidthClass = "min-w-[640px]",
}: {
  children: ReactNode;
  className?: string;
  minWidthClass?: string;
}) => (
  <div className={cn("rounded-lg border border-border", className)}>
    <div className="-mx-4 overflow-x-auto sm:mx-0">
      <div className={cn("inline-block min-w-full align-middle", minWidthClass)}>{children}</div>
    </div>
  </div>
);

const trendIconMap = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus,
} as const;

type MetricTone = "indigo" | "sky" | "emerald" | "amber";

type ReportMetric = {
  key: string;
  label: string;
  value: string;
  helper: string;
  icon: LucideIcon;
  tone: MetricTone;
  bars: number[];
};

type RangePreset = {
  id: string;
  label: string;
  from: string;
  to: string;
};

type ScheduleFormState = {
  label: string;
  cadence: "daily" | "weekly" | "monthly";
  dayOfWeek: string;
  dayOfMonth: string;
  time: string;
  recipients: string;
  target: ReportExportTargetInternal;
  format: "csv" | "json";
};

type ReportExportTargetInternal = Extract<
  ReportExportTarget,
  "reports:sales" | "reports:items" | "reports:closeout"
>;

const createEmptyScheduleForm = (): ScheduleFormState => ({
  label: "",
  cadence: "weekly",
  dayOfWeek: "1",
  dayOfMonth: "1",
  time: "06:00",
  recipients: "",
  target: "reports:sales",
  format: "csv",
});

const createRandomId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `preset-${Date.now().toString(36)}`;
};

const parseRecipientList = (value: string): string[] =>
  value
    .split(/[,;\n]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const ReportsPageV3 = () => {
  const { t } = useTranslation(["common", "dashboard", "events"]);
  const { toast } = useToast();
  const tenantStore = useTenantStore();
  const { subdomain } = useParams<{ subdomain?: string }>();
  const tenantSlug =
    subdomain ??
    tenantStore.activeSubdomain ??
    tenantStore.slug ??
    (typeof window !== "undefined" ? localStorage.getItem("tenantSlug") ?? undefined : undefined);
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [incidentSeverity, setIncidentSeverity] = useState("all");
  const [savedPresets, setSavedPresets] = useState<RangePreset[]>([]);
  const [presetsHydrated, setPresetsHydrated] = useState(false);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const presetsStorageKey = useMemo(
    () => `reports-presets:${tenantSlug ?? "default"}`,
    [tenantSlug],
  );
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormState>(() => createEmptyScheduleForm());
  const [snapshotTitle, setSnapshotTitle] = useState("");
  const [snapshotNotes, setSnapshotNotes] = useState("");
  const [snapshotDownloading, setSnapshotDownloading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setPresetsHydrated(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem(presetsStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as RangePreset[];
        if (Array.isArray(parsed)) {
          setSavedPresets(parsed);
        }
      }
    } catch (error) {
      console.warn("[ReportsPage] failed to hydrate presets", error);
    } finally {
      setPresetsHydrated(true);
    }
  }, [presetsStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !presetsHydrated) {
      return;
    }
    try {
      window.localStorage.setItem(presetsStorageKey, JSON.stringify(savedPresets));
    } catch (error) {
      console.warn("[ReportsPage] failed to persist presets", error);
    }
  }, [presetsHydrated, presetsStorageKey, savedPresets]);

  const cadenceOptions = useMemo(
    () => [
      { value: "daily", label: t("dashboard:reports.schedules.cadence.daily", { defaultValue: "Daily" }) },
      { value: "weekly", label: t("dashboard:reports.schedules.cadence.weekly", { defaultValue: "Weekly" }) },
      { value: "monthly", label: t("dashboard:reports.schedules.cadence.monthly", { defaultValue: "Monthly" }) },
    ],
    [t],
  );

  const dayOfWeekOptions = useMemo(
    () => [
      { value: "0", label: t("dashboard:reports.schedules.weekdays.sunday", { defaultValue: "Sunday" }) },
      { value: "1", label: t("dashboard:reports.schedules.weekdays.monday", { defaultValue: "Monday" }) },
      { value: "2", label: t("dashboard:reports.schedules.weekdays.tuesday", { defaultValue: "Tuesday" }) },
      { value: "3", label: t("dashboard:reports.schedules.weekdays.wednesday", { defaultValue: "Wednesday" }) },
      { value: "4", label: t("dashboard:reports.schedules.weekdays.thursday", { defaultValue: "Thursday" }) },
      { value: "5", label: t("dashboard:reports.schedules.weekdays.friday", { defaultValue: "Friday" }) },
      { value: "6", label: t("dashboard:reports.schedules.weekdays.saturday", { defaultValue: "Saturday" }) },
    ],
    [t],
  );

  const describeCadence = useCallback(
    (schedule: ReportSchedule) => {
      switch (schedule.cadence) {
        case "daily":
          return t("dashboard:reports.schedules.cadence.daily", { defaultValue: "Daily" });
        case "weekly":
          return t("dashboard:reports.schedules.cadence.weekly", { defaultValue: "Weekly" });
        case "monthly":
          return t("dashboard:reports.schedules.cadence.monthly", { defaultValue: "Monthly" });
        default:
          return schedule.cadence;
      }
    },
    [t],
  );

  const describeScheduleTarget = useCallback(
    (target: ReportExportTarget) => {
      switch (target) {
        case "reports:sales":
          return t("dashboard:reports.schedules.targets.sales", { defaultValue: "Sales summary" });
        case "reports:items":
          return t("dashboard:reports.schedules.targets.items", { defaultValue: "Item mix" });
        case "reports:closeout":
          return t("dashboard:reports.schedules.targets.closeout", { defaultValue: "Closeout report" });
        case "reports:incidents":
          return t("dashboard:reports.schedules.targets.incidents", { defaultValue: "Incident log" });
        case "orders":
          return t("dashboard:reports.schedules.targets.orders", { defaultValue: "Order export" });
        case "inventory":
          return t("dashboard:reports.schedules.targets.inventory", { defaultValue: "Inventory export" });
        default:
          return target;
      }
    },
    [t],
  );

  const salesExport = useExportJob({
    target: "reports:sales",
    getFilters: () => ({ from, to }),
    onSuccess: (job) => {
      if (job.delivery?.mode === "webhook") {
        const destinationKey = (job.delivery.destination ?? "accounting") as ExportDeliveryDestination;
        toast({
          title: t("dashboard:reports.exports.webhookSuccessTitle", { defaultValue: "Export dispatched" }),
          description: t("dashboard:reports.exports.webhookSuccessDescription", {
            defaultValue: "Automation queued for {{destination}}.",
            destination: destinationLabels[destinationKey] ?? destinationLabels.accounting,
          }),
        });
        return;
      }
      toast({
        title: t("dashboard:reports.exports.downloadSuccessTitle", { defaultValue: "Export ready" }),
        description: t("dashboard:reports.exports.downloadSuccessDescription", {
          defaultValue: "Download started.",
        }),
      });
    },
    onError: (error) =>
      toast({
        title: t("dashboard:reports.exports.failureTitle", {
          defaultValue: "Unable to export {{label}}",
          label: getExportTargetLabel("reports:sales"),
        }),
        description: error.message,
        variant: "destructive",
      }),
  });

  const itemsExport = useExportJob({
    target: "reports:items",
    getFilters: () => ({ from, to }),
    onSuccess: (job) => {
      if (job.delivery?.mode === "webhook") {
        const destinationKey = (job.delivery.destination ?? "warehouse") as ExportDeliveryDestination;
        toast({
          title: t("dashboard:reports.exports.webhookSuccessTitle", { defaultValue: "Export dispatched" }),
          description: t("dashboard:reports.exports.webhookSuccessDescription", {
            defaultValue: "Automation queued for {{destination}}.",
            destination: destinationLabels[destinationKey] ?? destinationLabels.warehouse,
          }),
        });
        return;
      }
      toast({
        title: t("dashboard:reports.exports.downloadSuccessTitle", { defaultValue: "Export ready" }),
        description: t("dashboard:reports.exports.downloadSuccessDescription", {
          defaultValue: "Download started.",
        }),
      });
    },
    onError: (error) =>
      toast({
        title: t("dashboard:reports.exports.failureTitle", {
          defaultValue: "Unable to export {{label}}",
          label: getExportTargetLabel("reports:items"),
        }),
        description: error.message,
        variant: "destructive",
      }),
  });

  const incidentsExport = useExportJob({
    target: "reports:incidents",
    getFilters: () => ({
      from,
      to,
      severity: incidentSeverity === "all" ? undefined : incidentSeverity,
    }),
    onSuccess: (job) => {
      if (job.delivery?.mode === "webhook") {
        const destinationKey = (job.delivery.destination ?? "warehouse") as ExportDeliveryDestination;
        toast({
          title: t("dashboard:reports.exports.webhookSuccessTitle", { defaultValue: "Export dispatched" }),
          description: t("dashboard:reports.exports.webhookSuccessDescription", {
            defaultValue: "Automation queued for {{destination}}.",
            destination: destinationLabels[destinationKey] ?? destinationLabels.warehouse,
          }),
        });
        return;
      }
      toast({
        title: t("dashboard:reports.exports.downloadSuccessTitle", { defaultValue: "Export ready" }),
        description: t("dashboard:reports.exports.downloadSuccessDescription", {
          defaultValue: "Download started.",
        }),
      });
    },
    onError: (error) =>
      toast({
        title: t("dashboard:reports.exports.failureTitle", {
          defaultValue: "Unable to export {{label}}",
          label: getExportTargetLabel("reports:incidents"),
        }),
        description: error.message,
        variant: "destructive",
      }),
  });

  const reportsQuery = useQuery({
    queryKey: ["reports", "overview", { from, to }],
    queryFn: ({ signal }) => getReports(from, to, signal),
  });

  const incidentQuery = useQuery({
    queryKey: ["reports", "incidents", { from, to, severity: incidentSeverity }],
    queryFn: ({ signal }) =>
      getIncidentReport({
        from,
        to,
        severity: incidentSeverity === "all" ? undefined : incidentSeverity,
        signal,
      }),
  });

  const analyticsQuery = useQuery({
    queryKey: ["analytics", "dashboard", tenantSlug],
    queryFn: ({ signal }) => {
      if (!tenantSlug) {
        throw new Error("missing tenant slug");
      }
      return fetchAnalyticsDashboard(tenantSlug, { signal, days: 30 });
    },
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
  });

  const schedulesQuery = useQuery({
    queryKey: ["reports", "schedules", tenantSlug ?? "default"],
    queryFn: ({ signal }) => listReportSchedules(signal),
  });

  const loyaltyProgramQuery = useQuery({
    queryKey: ["loyalty", "program", tenantSlug ?? "tenant-default"],
    queryFn: ({ signal }) => fetchEffectiveLoyaltyProgram({ signal }),
    enabled: Boolean(tenantSlug),
    staleTime: 60_000,
    retry: false,
  });

  // Notification preferences must be initialized before notification hooks use them
  const { preferences: restaurantPreferences } = useRestaurantSettings();
  const notificationsDisabled = restaurantPreferences ? !restaurantPreferences.notifications : false;

  const createScheduleMutation = useMutation({
    mutationFn: createReportSchedule,
    onSuccess: () => {
      schedulesQuery.refetch();
      toast({
        title: t("dashboard:reports.schedules.toastCreatedTitle", {
          defaultValue: "Schedule saved",
        }),
        description: t("dashboard:reports.schedules.toastCreatedDescription", {
          defaultValue: "We'll deliver the export automatically.",
        }),
      });
      setScheduleDialogOpen(false);
      setScheduleForm(createEmptyScheduleForm());
    },
    onError: (error: unknown) => {
      toast({
        title: t("dashboard:reports.schedules.toastErrorTitle", {
          defaultValue: "Unable to save schedule",
        }),
        description: error instanceof Error ? error.message : t("dashboard:reports.schedules.toastErrorDescription", {
          defaultValue: "Try again or adjust the recipients list.",
        }),
        variant: "destructive",
      });
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: deleteReportSchedule,
    onSuccess: () => {
      schedulesQuery.refetch();
      toast({
        title: t("dashboard:reports.schedules.toastDeletedTitle", {
          defaultValue: "Schedule removed",
        }),
        description: t("dashboard:reports.schedules.toastDeletedDescription", {
          defaultValue: "Automated delivery has been cancelled.",
        }),
      });
    },
    onError: (error: unknown) => {
      toast({
        title: t("dashboard:reports.schedules.toastDeleteErrorTitle", {
          defaultValue: "Unable to remove schedule",
        }),
        description: error instanceof Error ? error.message : t("dashboard:reports.schedules.toastDeleteErrorDescription", {
          defaultValue: "Please retry in a moment.",
        }),
        variant: "destructive",
      });
    },
  });

  const schedules = useMemo(
    () => schedulesQuery.data ?? [],
    [schedulesQuery.data],
  );

  const analytics = analyticsQuery.data;
  const latestQr = analytics?.qrScanTrend.at(-1) ?? null;
  const latestAov = analytics?.aovTrend.at(-1) ?? null;
  const prepSlaSummary = analytics?.prepSla ?? null;
  const churnSummary = analytics?.churn ?? null;
  const channelFulfillment = useMemo(
    () => analytics?.channelFulfillment ?? [],
    [analytics?.channelFulfillment],
  );
  const guestEngagement = analytics?.guestEngagement ?? null;
  const guestSummary = guestEngagement?.summary ?? null;
  const loyaltyProgram = loyaltyProgramQuery.data ?? null;
  const averageLoyaltyPointsPerOrder = useMemo(() => {
    if (!guestSummary || guestSummary.loyaltyOrders <= 0) {
      return 0;
    }
    return guestSummary.loyaltyPointsAwarded / guestSummary.loyaltyOrders;
  }, [guestSummary]);
  const pointsPerDollarEarnRate = useMemo(() => {
    if (!loyaltyProgram || loyaltyProgram.earnMode === "per_order") {
      return null;
    }
    const unit = loyaltyProgram.currencyUnit ?? 0;
    if (unit <= 0) {
      return null;
    }
    return loyaltyProgram.pointsPerCurrency / unit;
  }, [loyaltyProgram]);
  const guestTrendBarData = useMemo(() => {
    if (!guestEngagement || guestEngagement.daily.length === 0) {
      return [];
    }
    const recent = guestEngagement.daily.slice(-7);
    const maxTotal = recent.reduce((acc, point) => {
      const total = point.newGuests + point.returningGuests;
      return total > acc ? total : acc;
    }, 0);
    return recent.map((point) => {
      const totalGuests = point.newGuests + point.returningGuests;
      const percentage = maxTotal > 0 ? totalGuests / maxTotal : 0;
      return {
        key: point.day,
        label: dayFormatter.format(new Date(`${point.day}T00:00:00Z`)),
        valueLabel: quantityFormatter.format(totalGuests),
        percentage,
        percentageLabel: percentFormatter.format(percentage),
        hint: t("dashboard:reports.customerSegments.dailyHint", {
          defaultValue: "{{newGuests}} new · {{returningGuests}} returning",
          newGuests: quantityFormatter.format(point.newGuests),
          returningGuests: quantityFormatter.format(point.returningGuests),
        }),
      };
    });
  }, [guestEngagement, t]);
  const marketingBarData = useMemo(() => {
    if (!guestEngagement || guestEngagement.marketing.length === 0) {
      return [];
    }
    const totalRevenueCents = guestEngagement.marketing.reduce(
      (accumulator, entry) => accumulator + entry.revenueCents,
      0,
    );
    return guestEngagement.marketing.map((entry) => {
      const key = entry.channel || "UNKNOWN";
      const normalizedKey = key.trim().toUpperCase() || "UNKNOWN";
      const percentage = totalRevenueCents > 0 ? entry.revenueCents / totalRevenueCents : 0;
      const label = t(`dashboard:reports.customerSegments.channels.${normalizedKey}`, {
        defaultValue: normalizedKey,
      });
      return {
        key: normalizedKey,
        label,
        valueLabel: currencyFormatter.format(entry.revenueCents / 100),
        percentage,
        percentageLabel: percentFormatter.format(percentage),
        hint: t("dashboard:reports.customerSegments.marketingHint", {
          defaultValue: "{{orders}} orders · {{newGuests}} new guests",
          orders: quantityFormatter.format(entry.orders),
          newGuests: quantityFormatter.format(entry.newGuests),
        }),
        tone: "sky" as const,
      };
    });
  }, [guestEngagement, t]);
  const channelFulfillmentRows = useMemo(() => {
    if (channelFulfillment.length === 0) {
      return [];
    }
    return channelFulfillment.map((entry) => {
      const label = t(`dashboard:reports.fulfillmentChannels.segments.${entry.key}.label`, {
        defaultValue: entry.key === "onPremise" ? "On-premise" : "Marketplace & delivery",
      });
      const description = t(`dashboard:reports.fulfillmentChannels.segments.${entry.key}.description`, {
        defaultValue: entry.key === "onPremise" ? "QR/tablet, counter, and pickup flows." : "Third-party marketplace and courier handoffs.",
      });
      const revenueLabel = currencyFormatter.format(entry.revenueCents / 100);
      const averageOrderLabel = currencyFormatter.format(entry.averageOrderValueCents / 100);
      const onTimeRateValue = Math.max(0, Math.min(100, entry.slaOnTimeRate));
      const onTimeRateLabel = percentFormatter.format(Math.max(0, Math.min(1, entry.slaOnTimeRate / 100)));
      const breachRateLabel = percentFormatter.format(Math.max(0, Math.min(1, entry.slaBreachRate / 100)));
      const delayLabel =
        entry.slaAverageDelaySeconds > 0
          ? formatSecondsAsDuration(entry.slaAverageDelaySeconds)
          : t("dashboard:reports.fulfillmentChannels.metrics.noDelay", { defaultValue: "-" });
      return {
        key: entry.key,
        label,
        description,
        ordersLabel: quantityFormatter.format(entry.orders),
        revenueLabel,
        averageOrderLabel,
        onTimeRateValue,
        onTimeRateLabel,
        evaluatedLabel: quantityFormatter.format(entry.slaEvaluatedOrders),
        breachesLabel: quantityFormatter.format(entry.slaBreaches),
        breachRateLabel,
        delayLabel,
      };
    });
  }, [channelFulfillment, t]);

  const summary = reportsQuery.data?.summary ?? {
    total_revenue_cents: 0,
    gross_revenue_cents: 0,
    net_revenue_cents: 0,
    discount_cents: 0,
    tax_cents: 0,
    service_charge_cents: 0,
    surcharge_cents: 0,
    gratuity_cents: 0,
    refunds_cents: 0,
    orders: 0,
    avg_order_value_cents: 0,
  };
  const tenders = reportsQuery.data?.tenders ?? {
    cash_cents: 0,
    card_cents: 0,
    tips_cents: 0,
    refunds_cents: 0,
  };
  const cateringSnapshot = reportsQuery.data?.catering ?? null;

  const cateringOverview = useMemo(
    () =>
      buildCateringOverview(cateringSnapshot, {
        t,
        currencyFormatter,
        percentFormatter,
      }),
    [cateringSnapshot, t],
  );
  const zReport = reportsQuery.data?.z_report ?? {
    opening_float_cents: 0,
    cash_sales_cents: 0,
    cash_refunds_cents: 0,
    drops_cents: 0,
    payouts_cents: 0,
    closing_cash_cents: 0,
    gross_sales_cents: 0,
    net_sales_cents: 0,
    discount_cents: 0,
    tax_cents: 0,
    service_charge_cents: 0,
    surcharge_cents: 0,
  };
  const salesTotals = reportsQuery.data?.sales_totals ?? {
    subtotal_cents: 0,
    discount_cents: 0,
    net_sales_cents: 0,
    tax_cents: 0,
    service_charge_cents: 0,
    surcharge_cents: 0,
    tips_cents: 0,
  };
  const daypartBreakdown = useMemo(
    () => reportsQuery.data?.daypart_breakdown ?? [],
    [reportsQuery.data?.daypart_breakdown],
  );
  const channelBreakdown = useMemo(
    () => reportsQuery.data?.channel_breakdown ?? [],
    [reportsQuery.data?.channel_breakdown],
  );
  const periodComparison = reportsQuery.data?.period_comparison ?? null;
  const sameStoreComparisons = useMemo(
    () => reportsQuery.data?.same_store ?? [],
    [reportsQuery.data?.same_store],
  );
  const salesByDay = useMemo(
    () => reportsQuery.data?.sales_by_day ?? [],
    [reportsQuery.data?.sales_by_day],
  );
  const topItems = useMemo(
    () => reportsQuery.data?.top_items ?? [],
    [reportsQuery.data?.top_items],
  );
  const categoryMix = useMemo(
    () => reportsQuery.data?.category_mix ?? [],
    [reportsQuery.data?.category_mix],
  );
  const { orderedCategories, metadata: categoryRecommendationMetadata } = useMenuCategoryOrdering(
    categoryMix,
    reportsQuery.data?.category_recommendations ?? null,
  );
  const modifierMix = useMemo(
    () => reportsQuery.data?.modifier_mix ?? [],
    [reportsQuery.data?.modifier_mix],
  );
  const comboMix = useMemo(
    () => reportsQuery.data?.combo_mix ?? [],
    [reportsQuery.data?.combo_mix],
  );
  const locationMargins = useMemo(
    () => reportsQuery.data?.location_margins ?? [],
    [reportsQuery.data?.location_margins],
  );
  const inventoryInsights = (reportsQuery.data?.inventory_insights ?? {
    estimated_cogs_cents: 0,
    estimated_margin_cents: 0,
    waste: {
      events: 0,
      quantity: 0,
      estimated_cost_cents: 0,
      top_reasons: [] as Array<{ reason: string; count: number }>,
      top_items: [] as Array<{ name: string; location_name: string | null; quantity: number; estimated_cost_cents: number }>,
    },
    prep: {
      events: 0,
      quantity: 0,
      estimated_cost_cents: 0,
      avg_batch_quantity: null as number | null,
      top_items: [] as Array<{ name: string; location_name: string | null; quantity: number; estimated_cost_cents: number }>,
      recent_batches: [] as Array<{
        id: string;
        name: string;
        location_name: string | null;
        quantity: number;
        estimated_cost_cents: number;
        reference: string | null;
        occurred_at: number | null;
      }>,
      cadence: {
        daily: [] as Array<{ date: string; events: number; total_quantity: number }>,
        hourly: [] as Array<{ hour: number; events: number; total_quantity: number }>,
      },
    },
    usage_variance: {
      total_variance_quantity: 0,
      estimated_variance_cost_cents: 0,
      products: [] as Array<{
        product_id: string | null;
        product_name: string;
        location_id: string | null;
        location_name: string | null;
        sale_quantity: number;
        other_quantity: number;
        variance_quantity: number;
        estimated_variance_cost_cents: number;
      }>,
    },
    par_breaches: [] as Array<{
      level_id: string;
      product_id: string | null;
      product_name: string;
      location_id: string | null;
      location_name: string | null;
      on_hand: number;
      reorder_point: number | null;
      deficit: number;
      coverage_days: number | null;
      severity: 'critical' | 'warning';
      updated_at: number | null;
    }>,
    low_stock: [] as Array<{
      level_id: string;
      product_id: string | null;
      product_name: string;
      location_id: string | null;
      location_name: string | null;
      on_hand: number;
      reorder_point: number | null;
      daily_burn_rate: number | null;
      updated_at: number | null;
    }>,
    depletion_risks: [] as Array<{
      level_id: string;
      product_id: string | null;
      product_name: string;
      location_id: string | null;
      location_name: string | null;
      on_hand: number;
      reorder_point: number | null;
      daily_burn_rate: number | null;
      updated_at: number | null;
    }>,
  }) as ReportsInventoryInsights;
  const laborInsights = (reportsQuery.data?.labor_insights ?? {
    regular_hours: 0,
    overtime_hours: 0,
    premium_hours: 0,
    total_hours: 0,
    overtime_share: null as number | null,
    estimated_labor_cost_cents: 0,
    labor_percent: null as number | null,
    total_tips_cents: 0,
    gratuity_by_role: [] as Array<{ role: string; tips_cents: number; percentage: number | null }>,
    open_exceptions: 0,
  }) as ReportsLaborInsights;
  const wasteSummary = inventoryInsights.waste ?? {
    events: 0,
    quantity: 0,
    estimated_cost_cents: 0,
    top_reasons: [] as Array<{ reason: string; count: number }>,
    top_items: [] as Array<{ name: string; location_name: string | null; quantity: number; estimated_cost_cents: number }>,
  };
  const prepActivitySummary = inventoryInsights.prep ?? {
    events: 0,
    quantity: 0,
    estimated_cost_cents: 0,
    avg_batch_quantity: null as number | null,
    top_items: [] as Array<{ name: string; location_name: string | null; quantity: number; estimated_cost_cents: number }>,
    recent_batches: [] as Array<{
      id: string;
      name: string;
      location_name: string | null;
      quantity: number;
      estimated_cost_cents: number;
      reference: string | null;
      occurred_at: number | null;
    }>,
    cadence: {
      daily: [] as Array<{ date: string; events: number; total_quantity: number }>,
      hourly: [] as Array<{ hour: number; events: number; total_quantity: number }>,
    },
  };
  const usageVariance = inventoryInsights.usage_variance ?? {
    total_variance_quantity: 0,
    estimated_variance_cost_cents: 0,
    products: [] as Array<{
      product_id: string | null;
      product_name: string;
      location_id: string | null;
      location_name: string | null;
      sale_quantity: number;
      other_quantity: number;
      variance_quantity: number;
      estimated_variance_cost_cents: number;
    }>,
  };
  const parBreaches = useMemo(
    () => inventoryInsights.par_breaches ?? [],
    [inventoryInsights.par_breaches],
  );
  const totalVarianceQuantity = usageVariance.total_variance_quantity ?? 0;
  const totalVarianceCostCents = usageVariance.estimated_variance_cost_cents ?? 0;
  const formatQuantity = useCallback((value: number) => quantityFormatter.format(Math.max(value, 0)), []);
  const usageVarianceProducts = useMemo(
    () => (usageVariance.products ?? []).slice(0, 8),
    [usageVariance.products],
  );
  const topParBreaches = useMemo(() => parBreaches.slice(0, 5), [parBreaches]);
  const formatParBreachNotification = useCallback(
    (breach: ParBreachNotificationInput) => {
      const locationLabel = breach.location_name
        ? breach.location_name
        : t('dashboard:reports.menuIntelligence.location.unknown', {
            defaultValue: 'Unassigned location',
          });

      const title = t('dashboard:reports.menuIntelligence.notifications.parBreach.title', {
        defaultValue: 'Critical stock alert: {{product}}',
        product: breach.product_name,
      });

      const descriptionKey = breach.location_name
        ? 'dashboard:reports.menuIntelligence.notifications.parBreach.description'
        : 'dashboard:reports.menuIntelligence.notifications.parBreach.descriptionNoLocation';

      const description = t(descriptionKey, {
        defaultValue: breach.location_name
          ? '{{location}} has {{onHand}} on hand (par {{reorder}}).'
          : '{{product}} is under par with {{onHand}} on hand (par {{reorder}}).',
        location: locationLabel,
        product: breach.product_name,
        onHand: formatQuantity(breach.on_hand ?? 0),
        reorder: formatQuantity(breach.reorder_point ?? 0),
      });

      return {
        title,
        description,
        variant: 'destructive' as const,
      };
    },
    [formatQuantity, t],
  );

  useParBreachNotifications(parBreaches, {
    disabled: notificationsDisabled || !reportsQuery.isSuccess || parBreaches.length === 0,
    formatNotification: formatParBreachNotification,
  });
  const kpiGuardrails = useMemo<KpiGuardrailNotificationInput[]>(() => {
    if (!analytics) {
      return [];
    }

    const generatedAtMs =
      typeof analytics.generatedAt === "string" ? Date.parse(analytics.generatedAt) || Date.now() : Date.now();
    const entries: KpiGuardrailNotificationInput[] = [];

    if (prepSlaSummary && prepSlaSummary.evaluatedOrders >= 10) {
      const breachRate = prepSlaSummary.breachRate ?? 0;
      const severity = breachRate >= 25 ? "critical" : breachRate >= 15 ? "warning" : null;
      if (severity) {
        entries.push({
          id: "prep-sla",
          title: t("dashboard:reports.notifications.kpi.prepSla.title", {
            defaultValue: "Kitchen prep time spikes",
          }),
          description: t("dashboard:reports.notifications.kpi.prepSla.description", {
            defaultValue: "Prep SLA breach rate is {{percent}}%.",
            percent: percentFormatter.format(Math.min(1, Math.max(0, breachRate / 100))),
          }),
          severity,
          breachedAt: generatedAtMs,
        });
      }
    }

    const marketplaceSegment = channelFulfillment.find((entry) => entry.key === "marketplaceDelivery");
    if (marketplaceSegment && marketplaceSegment.slaEvaluatedOrders >= 5) {
      const onTimeRate = marketplaceSegment.slaOnTimeRate ?? 0;
      const severity = onTimeRate <= 65 ? "critical" : onTimeRate <= 80 ? "warning" : null;
      if (severity) {
        entries.push({
          id: "marketplace-on-time",
          title: t("dashboard:reports.notifications.kpi.marketplaceOnTime.title", {
            defaultValue: "Marketplace SLA at risk",
          }),
          description: t("dashboard:reports.notifications.kpi.marketplaceOnTime.description", {
            defaultValue: "Only {{percent}}% of marketplace orders are on time.",
            percent: percentFormatter.format(Math.min(1, Math.max(0, onTimeRate / 100))),
          }),
          severity,
          breachedAt: generatedAtMs,
        });
      }
    }

    if (analytics.churn && analytics.churn.activeCustomers > 0) {
      const churnRate = analytics.churn.churnRate ?? 0;
      const severity = churnRate >= 40 ? "critical" : churnRate >= 25 ? "warning" : null;
      if (severity) {
        entries.push({
          id: "guest-churn",
          title: t("dashboard:reports.notifications.kpi.churn.title", {
            defaultValue: "Guest churn rising",
          }),
          description: t("dashboard:reports.notifications.kpi.churn.description", {
            defaultValue: "{{percent}}% of active guests churned in this period.",
            percent: percentFormatter.format(Math.min(1, Math.max(0, churnRate / 100))),
          }),
          severity,
          breachedAt: generatedAtMs,
        });
      }
    }

    return entries;
  }, [analytics, channelFulfillment, prepSlaSummary, t]);
  useKpiGuardrailNotifications(kpiGuardrails, {
    disabled: notificationsDisabled || !reportsQuery.isSuccess,
    maxNotifications: 2,
  });
  const prepCadenceDaily = useMemo(
    () => (prepActivitySummary.cadence?.daily ?? []).slice(0, 7),
    [prepActivitySummary.cadence?.daily],
  );
  const prepCadenceHourly = useMemo(
    () => prepActivitySummary.cadence?.hourly ?? [],
    [prepActivitySummary.cadence?.hourly],
  );

  const categoryInsights = useMemo(
    () =>
      orderedCategories.slice(0, 8).map((entry, index) => ({
        key: entry.key ?? `category-${index}`,
        name: entry.entry.category_name ?? t('dashboard:reports.menuIntelligence.category.unknown', {
          defaultValue: 'Uncategorised',
        }),
        quantity: entry.entry.quantity ?? 0,
        revenueCents: entry.entry.revenue_cents ?? 0,
        shareRatio: entry.entry.share_ratio ?? 0,
        marginCents: entry.entry.margin_cents ?? 0,
        marginRate: entry.entry.margin_rate ?? null,
        recommendedRank: entry.recommendedRank,
        recommendationReason: entry.recommendationReason ?? null,
      })),
    [orderedCategories, t],
  );
  const showCategoryRecommendationColumn = Boolean(categoryRecommendationMetadata);

  const modifierInsights = useMemo(
    () =>
      modifierMix.slice(0, 8).map((entry) => ({
        key: entry.modifier_name ?? 'modifier',
        name: entry.modifier_name,
        attachCount: entry.attach_count ?? 0,
        revenueCents: entry.revenue_cents ?? 0,
        marginCents: entry.margin_cents ?? 0,
        marginRate: entry.margin_rate ?? null,
        attachRate: entry.attach_rate ?? null,
        shareRatio: entry.share_ratio ?? 0,
      })),
    [modifierMix],
  );

  const comboInsights = useMemo(
    () =>
      comboMix.slice(0, 8).map((entry) => ({
        key: entry.key,
        label: Array.isArray(entry.categories) && entry.categories.length > 0
          ? entry.categories.join(' + ')
          : entry.key,
        orders: entry.orders ?? 0,
        revenueCents: entry.revenue_cents ?? 0,
        marginCents: entry.margin_cents ?? 0,
        marginRate: entry.margin_rate ?? null,
        shareRatio: entry.share_ratio ?? null,
        topItems: Array.isArray(entry.top_items) ? entry.top_items.slice(0, 3) : [],
      })),
    [comboMix],
  );

  const locationMarginInsights = useMemo(
    () =>
      locationMargins.slice(0, 6).map((entry) => ({
        key: entry.location_id ?? entry.location_name ?? 'location',
        name: entry.location_name ?? t('dashboard:reports.menuIntelligence.location.unknown', {
          defaultValue: 'Unassigned location',
        }),
        orderCount: entry.order_count ?? 0,
        revenueCents: entry.revenue_cents ?? 0,
        marginCents: entry.margin_cents ?? 0,
        marginRate: entry.margin_rate ?? null,
      })),
    [locationMargins, t],
  );
  const lowStockAlerts = useMemo(
    () => (inventoryInsights.low_stock ?? []).slice(0, 5),
    [inventoryInsights.low_stock],
  );
  const depletionAlerts = useMemo(
    () => (inventoryInsights.depletion_risks ?? []).slice(0, 5),
    [inventoryInsights.depletion_risks],
  );
  const laborGratuityByRole = useMemo(
    () => (laborInsights.gratuity_by_role ?? []).slice(0, 5),
    [laborInsights.gratuity_by_role],
  );
  const revenueSeries = salesByDay.map((entry) => Number(entry.revenue_cents ?? 0));
  const ordersSeries = salesByDay.map((entry) => Number(entry.orders ?? 0));
  const averageTicketSeries = salesByDay.map((entry) => {
    const revenue = Number(entry.revenue_cents ?? 0);
    const orders = Number(entry.orders ?? 0);
    return orders > 0 ? Math.round(revenue / orders) : 0;
  });

  const closeoutExport = useExportJob({
    target: "reports:closeout",
    getFilters: () => ({ from, to }),
    onSuccess: () =>
      toast({
        title: t('dashboard:reports.closeout.toastSuccessTitle', { defaultValue: 'Closeout export ready' }),
        description: t('dashboard:reports.closeout.toastSuccessDescription', { defaultValue: 'Closeout download started.' }),
      }),
    onError: (error) =>
      toast({
        title: t('dashboard:reports.closeout.toastErrorTitle', { defaultValue: 'Unable to export closeout' }),
        description: error.message,
        variant: 'destructive',
      }),
  });

  const revenueBars = buildNormalizedBars(revenueSeries);
  const ordersBars = buildNormalizedBars(ordersSeries);
  const avgOrderBars = buildNormalizedBars(averageTicketSeries);

  const reportsErrorMessage = reportsQuery.isError
    ? ((reportsQuery.error as Error)?.message ?? "The server has no data for this range yet.")
    : null;

  const metrics = useMemo<ReportMetric[]>(() => {
    const grossRevenueCents = summary.gross_revenue_cents ?? summary.total_revenue_cents ?? 0;
    const netRevenueCents = summary.net_revenue_cents ?? summary.total_revenue_cents ?? 0;
    const grossRevenueLabel = currencyFormatter.format(grossRevenueCents / 100);
    const netRevenueLabel = currencyFormatter.format(netRevenueCents / 100);
    const avgOrderLabel = currencyFormatter.format((summary.avg_order_value_cents ?? 0) / 100);
    const totalOrders = summary.orders ?? 0;
    const coverageDays = salesByDay.length;
    const rangeHelper =
      coverageDays === 1
        ? t("dashboard:reports.metrics.rangeSingle", { defaultValue: "Single day range" })
        : t("dashboard:reports.metrics.rangeRange", {
            defaultValue: "{{count}} days selected",
            count: coverageDays,
          });

    return [
      {
        key: 'total-orders',
        label: t('dashboard:reports.metrics.totalOrders', { defaultValue: 'Total Orders' }),
        value: totalOrders.toLocaleString(),
        helper: rangeHelper,
        icon: TrendingUp,
        tone: 'indigo',
        bars: ordersBars.length ? ordersBars : Array.from({ length: 8 }, () => 0),
      },
      {
        key: 'gross-revenue',
        label: t('dashboard:reports.metrics.grossRevenue', { defaultValue: 'Gross Revenue' }),
        value: grossRevenueLabel,
        helper: t('dashboard:reports.metrics.grossRevenueHelper', {
          defaultValue: 'Across the selected date range',
        }),
        icon: BarChart3,
        tone: 'emerald',
        bars: revenueBars.length ? revenueBars : Array.from({ length: 8 }, () => 0),
      },
      {
        key: 'net-revenue',
        label: t('dashboard:reports.metrics.netRevenue', { defaultValue: 'Net Revenue' }),
        value: netRevenueLabel,
        helper: t('dashboard:reports.metrics.netRevenueHelper', {
          defaultValue: 'After discounts and refunds',
        }),
        icon: ListOrdered,
        tone: 'sky',
        bars: revenueBars.length ? revenueBars : Array.from({ length: 8 }, () => 0),
      },
      {
        key: 'average-ticket',
        label: t('dashboard:reports.metrics.averageTicket', { defaultValue: 'Average Ticket' }),
        value: avgOrderLabel,
        helper: t('dashboard:reports.metrics.averageTicketHelper', {
          defaultValue: 'Average order value over the range',
        }),
        icon: CalendarRange,
        tone: 'amber',
        bars: avgOrderBars.length ? avgOrderBars : Array.from({ length: 8 }, () => 0),
      },
    ];
  }, [avgOrderBars, ordersBars, revenueBars, salesByDay.length, summary.avg_order_value_cents, summary.gross_revenue_cents, summary.net_revenue_cents, summary.orders, summary.total_revenue_cents, t]);

  const tenderMetrics = useMemo(() => {
    const refundsCents = tenders.refunds_cents ?? 0;
    const normalizedRefunds = refundsCents === 0 ? 0 : -refundsCents;

    return [
      {
        key: 'cash',
        label: t('dashboard:reports.tenders.labels.cash', { defaultValue: 'Cash' }),
        value: currencyFormatter.format((tenders.cash_cents ?? 0) / 100),
        helper: t('dashboard:reports.tenders.helpers.cash', {
          defaultValue: 'Drawer settlements captured during the range',
        }),
      },
      {
        key: 'card',
        label: t('dashboard:reports.tenders.labels.card', { defaultValue: 'Card' }),
        value: currencyFormatter.format((tenders.card_cents ?? 0) / 100),
        helper: t('dashboard:reports.tenders.helpers.card', {
          defaultValue: 'Processed card and digital wallet payments',
        }),
      },
      {
        key: 'tips',
        label: t('dashboard:reports.tenders.labels.tips', { defaultValue: 'Tips' }),
        value: currencyFormatter.format((tenders.tips_cents ?? 0) / 100),
        helper: t('dashboard:reports.tenders.helpers.tips', {
          defaultValue: 'Tip pool accrual for payroll hand-off',
        }),
      },
      {
        key: 'refunds',
        label: t('dashboard:reports.tenders.labels.refunds', { defaultValue: 'Refunds' }),
        value: currencyFormatter.format(normalizedRefunds / 100),
        helper: t('dashboard:reports.tenders.helpers.refunds', {
          defaultValue: 'Refunds and voids returned to guests',
        }),
      },
    ];
  }, [t, tenders.card_cents, tenders.cash_cents, tenders.refunds_cents, tenders.tips_cents]);

  const zSummaryMetrics = useMemo(
    () => [
      {
        key: 'gross',
        label: t('dashboard:reports.zSummary.labels.grossSales', { defaultValue: 'Gross sales' }),
        value: currencyFormatter.format((zReport.gross_sales_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.grossSales', {
          defaultValue: 'Before discounts and adjustments',
        }),
      },
      {
        key: 'net',
        label: t('dashboard:reports.zSummary.labels.netSales', { defaultValue: 'Net sales' }),
        value: currencyFormatter.format((zReport.net_sales_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.netSales', {
          defaultValue: 'After discounts and refunds',
        }),
      },
      {
        key: 'discount',
        label: t('dashboard:reports.zSummary.labels.discounts', { defaultValue: 'Discounts' }),
        value: currencyFormatter.format((zReport.discount_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.discounts', {
          defaultValue: 'Promotions and comps applied',
        }),
      },
      {
        key: 'tax',
        label: t('dashboard:reports.zSummary.labels.tax', { defaultValue: 'Tax' }),
        value: currencyFormatter.format((zReport.tax_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.tax', {
          defaultValue: 'Sales tax accrued for the period',
        }),
      },
      {
        key: 'service',
        label: t('dashboard:reports.zSummary.labels.service', { defaultValue: 'Service fees' }),
        value: currencyFormatter.format((zReport.service_charge_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.service', {
          defaultValue: 'Service and gratuity charges retained',
        }),
      },
      {
        key: 'surcharge',
        label: t('dashboard:reports.zSummary.labels.surcharge', { defaultValue: 'Surcharges' }),
        value: currencyFormatter.format((zReport.surcharge_cents ?? 0) / 100),
        helper: t('dashboard:reports.zSummary.helpers.surcharge', {
          defaultValue: 'Additional fees collected',
        }),
      },
    ],
    [
      t,
      zReport.discount_cents,
      zReport.gross_sales_cents,
      zReport.net_sales_cents,
      zReport.service_charge_cents,
      zReport.surcharge_cents,
      zReport.tax_cents,
    ],
  );

  const revenueBreakdown = useMemo(() => {
    const grossCents = summary.gross_revenue_cents ?? salesTotals.subtotal_cents ?? 0;
    const netCents = summary.net_revenue_cents ?? salesTotals.net_sales_cents ?? 0;
    const discountsCents = salesTotals.discount_cents ?? summary.discount_cents ?? 0;
    const refundsCents = summary.refunds_cents ?? 0;
    const netShare = grossCents > 0 ? netCents / grossCents : 0;

    return {
      netShare,
      netShareLabel: percentFormatter.format(netShare),
      items: [
        {
          key: 'gross',
          label: t('dashboard:reports.revenueBreakdown.gross', { defaultValue: 'Gross revenue' }),
          value: currencyFormatter.format(grossCents / 100),
        },
        {
          key: 'net',
          label: t('dashboard:reports.revenueBreakdown.net', { defaultValue: 'Net revenue' }),
          value: currencyFormatter.format(netCents / 100),
        },
        {
          key: 'discounts',
          label: t('dashboard:reports.revenueBreakdown.discounts', { defaultValue: 'Discounts' }),
          value: currencyFormatter.format(-discountsCents / 100),
        },
        {
          key: 'refunds',
          label: t('dashboard:reports.revenueBreakdown.refunds', { defaultValue: 'Refunds & voids' }),
          value: currencyFormatter.format(-refundsCents / 100),
        },
      ],
    };
  }, [
    salesTotals.discount_cents,
    salesTotals.net_sales_cents,
    salesTotals.subtotal_cents,
    summary.discount_cents,
    summary.gross_revenue_cents,
    summary.net_revenue_cents,
    summary.refunds_cents,
    t,
  ]);

  const waterfallSteps = useMemo(() => {
    const subtotal = salesTotals.subtotal_cents ?? 0;
    const discounts = salesTotals.discount_cents ?? 0;
    const net = salesTotals.net_sales_cents ?? 0;
    const tax = salesTotals.tax_cents ?? 0;
    const service = salesTotals.service_charge_cents ?? 0;
    const surcharge = salesTotals.surcharge_cents ?? 0;
    const tips = salesTotals.tips_cents ?? 0;
    const totalCollected = net + tax + service + surcharge + tips;

    return [
      {
        key: 'subtotal',
        label: t('dashboard:reports.waterfall.subtotal', { defaultValue: 'Subtotal (gross)' }),
        kind: 'base',
        valueLabel: currencyFormatter.format(subtotal / 100),
        deltaValue: subtotal,
      },
      {
        key: 'discounts',
        label: t('dashboard:reports.waterfall.discounts', { defaultValue: 'Discounts' }),
        kind: 'decrease',
        valueLabel: currencyFormatter.format(-discounts / 100),
        deltaValue: -discounts,
      },
      {
        key: 'net',
        label: t('dashboard:reports.waterfall.net', { defaultValue: 'Net sales' }),
        kind: 'total',
        valueLabel: currencyFormatter.format(net / 100),
        deltaValue: net,
      },
      {
        key: 'tax',
        label: t('dashboard:reports.waterfall.tax', { defaultValue: 'Tax' }),
        kind: 'increase',
        valueLabel: currencyFormatter.format(tax / 100),
        deltaValue: tax,
      },
      {
        key: 'service',
        label: t('dashboard:reports.waterfall.service', { defaultValue: 'Service fees' }),
        kind: 'increase',
        valueLabel: currencyFormatter.format(service / 100),
        deltaValue: service,
      },
      {
        key: 'surcharge',
        label: t('dashboard:reports.waterfall.surcharge', { defaultValue: 'Surcharges' }),
        kind: 'increase',
        valueLabel: currencyFormatter.format(surcharge / 100),
        deltaValue: surcharge,
      },
      {
        key: 'tips',
        label: t('dashboard:reports.waterfall.tips', { defaultValue: 'Tips' }),
        kind: 'increase',
        valueLabel: currencyFormatter.format(tips / 100),
        deltaValue: tips,
      },
      {
        key: 'total',
        label: t('dashboard:reports.waterfall.total', { defaultValue: 'Total collected' }),
        kind: 'total',
        valueLabel: currencyFormatter.format(totalCollected / 100),
        deltaValue: totalCollected,
      },
    ];
  }, [
    salesTotals.discount_cents,
    salesTotals.net_sales_cents,
    salesTotals.service_charge_cents,
    salesTotals.subtotal_cents,
    salesTotals.surcharge_cents,
    salesTotals.tax_cents,
    salesTotals.tips_cents,
    t,
  ]);

  const daypartDistribution = useMemo(() => {
    if (daypartBreakdown.length === 0) {
      return [];
    }
    const total = daypartBreakdown.reduce(
      (carry, entry) => carry + (entry.total_revenue_cents ?? 0),
      0,
    );

    return daypartBreakdown
      .map((entry) => {
        const revenueCents = entry.total_revenue_cents ?? 0;
        const share = total > 0 ? revenueCents / total : 0;
        const startLabel = formatMinuteLabel(entry.start_minute ?? 0);
        const endLabel = formatMinuteLabel(entry.end_minute ?? 0);
        const rangeLabel = t('dashboard:reports.dayparts.range', {
          start: startLabel,
          end: endLabel,
          defaultValue: `${startLabel} – ${endLabel}`,
        });
        const ordersLabel = t('dashboard:reports.dayparts.orders', {
          count: entry.orders ?? 0,
          defaultValue:
            (entry.orders ?? 0) === 1 ? '1 order' : `${entry.orders ?? 0} orders`,
        });
        const hint = t('dashboard:reports.dayparts.hint', {
          range: rangeLabel,
          orders: ordersLabel,
          defaultValue: `${rangeLabel} · ${ordersLabel}`,
        });

        return {
          key: entry.key,
          label: t(`dashboard:reports.dayparts.labels.${entry.key}`, {
            defaultValue: entry.label ?? entry.key,
          }),
          valueLabel: currencyFormatter.format(revenueCents / 100),
          percentageLabel: percentFormatter.format(share),
          percentage: share,
          hint,
        };
      })
      .sort((a, b) => b.percentage - a.percentage);
  }, [daypartBreakdown, t]);

  const channelDistribution = useMemo(() => {
    if (channelBreakdown.length === 0) {
      return [];
    }
    const total = channelBreakdown.reduce(
      (carry, entry) => carry + (entry.total_revenue_cents ?? 0),
      0,
    );

    return channelBreakdown
      .map((entry) => {
        const revenueCents = entry.total_revenue_cents ?? 0;
        const share = total > 0 ? revenueCents / total : 0;
        const key = entry.key && entry.key.length > 0 ? entry.key : 'unknown';
        const fallbackLabel =
          entry.label ?? key.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
        const label = t(`dashboard:reports.channels.labels.${key}`, {
          defaultValue: fallbackLabel,
        });
        const ordersLabel = t('dashboard:reports.channels.orders', {
          count: entry.orders ?? 0,
          defaultValue:
            (entry.orders ?? 0) === 1 ? '1 order' : `${entry.orders ?? 0} orders`,
        });

        return {
          key,
          label,
          valueLabel: currencyFormatter.format(revenueCents / 100),
          percentageLabel: percentFormatter.format(share),
          percentage: share,
          hint: ordersLabel,
        };
      })
      .sort((a, b) => b.percentage - a.percentage);
  }, [channelBreakdown, t]);

  const comparisonMetrics = useMemo(() => {
    if (!periodComparison) {
      return [];
    }

    const configs = [
      {
        key: 'gross',
        label: t('dashboard:reports.periodComparison.metrics.gross', { defaultValue: 'Gross revenue' }),
        metric: periodComparison.metrics.gross_revenue_cents,
        isCurrency: true,
      },
      {
        key: 'net',
        label: t('dashboard:reports.periodComparison.metrics.net', { defaultValue: 'Net revenue' }),
        metric: periodComparison.metrics.net_revenue_cents,
        isCurrency: true,
      },
      {
        key: 'orders',
        label: t('dashboard:reports.periodComparison.metrics.orders', { defaultValue: 'Orders' }),
        metric: periodComparison.metrics.orders,
        isCurrency: false,
      },
      {
        key: 'average-ticket',
        label: t('dashboard:reports.periodComparison.metrics.averageTicket', { defaultValue: 'Average ticket' }),
        metric: periodComparison.metrics.avg_order_value_cents,
        isCurrency: true,
      },
    ] as const;

    return configs.map((config) => {
      const { metric } = config;
      const formatValue = (value: number) =>
        config.isCurrency ? currencyFormatter.format(value / 100) : value.toLocaleString();
      const currentLabel = formatValue(metric.current);
      const previousLabel = formatValue(metric.previous);
      const deltaLabelRaw = formatValue(metric.delta);
      const deltaLabel = metric.delta === 0
        ? (config.isCurrency ? currencyFormatter.format(0) : '0')
        : metric.delta > 0
        ? `+${deltaLabelRaw}`
        : deltaLabelRaw;
      const percentLabel = formatDeltaPercentLabel(metric.delta_percent);
      const trend = determineTrend(metric.delta_percent, metric.delta);
      return {
        key: config.key,
        label: config.label,
        currentLabel,
        previousLabel,
        deltaLabel,
        percentLabel,
        trend,
      };
    });
  }, [periodComparison, t]);

  const sameStoreRows = useMemo(() => {
    if (sameStoreComparisons.length === 0) {
      return [];
    }

    return sameStoreComparisons.map((entry) => {
      const revenueCurrent = currencyFormatter.format(entry.revenue_cents.current / 100);
      const revenueDeltaFormatted = currencyFormatter.format(entry.revenue_cents.delta / 100);
      const revenueDeltaLabel = entry.revenue_cents.delta === 0
        ? currencyFormatter.format(0)
        : entry.revenue_cents.delta > 0
        ? `+${revenueDeltaFormatted}`
        : revenueDeltaFormatted;
      const revenuePercentLabel = formatDeltaPercentLabel(entry.revenue_cents.delta_percent);
      const ordersCurrent = entry.orders.current.toLocaleString();
      const ordersDeltaFormatted = entry.orders.delta.toLocaleString();
      const ordersDeltaLabel = entry.orders.delta === 0
        ? '0'
        : entry.orders.delta > 0
        ? `+${ordersDeltaFormatted}`
        : ordersDeltaFormatted;
      const ordersPercentLabel = formatDeltaPercentLabel(entry.orders.delta_percent);
      const trend = entry.trend ?? determineTrend(entry.revenue_cents.delta_percent, entry.revenue_cents.delta);
      const TrendIcon = trendIconMap[trend];
      const trendLabels = {
        up: t('dashboard:reports.sameStore.trend.up', { defaultValue: 'Improving' }),
        down: t('dashboard:reports.sameStore.trend.down', { defaultValue: 'Needs attention' }),
        flat: t('dashboard:reports.sameStore.trend.flat', { defaultValue: 'Stable' }),
      } as const;
      return {
        id: entry.location_id ?? `unknown-${entry.location_name}`,
        name: entry.location_name,
        revenueCurrent,
        revenueDeltaLabel,
        revenuePercentLabel,
        ordersCurrent,
        ordersDeltaLabel,
        ordersPercentLabel,
        trend,
        TrendIcon,
        trendLabel: trendLabels[trend],
        trendClass: trendStyleMap[trend],
        trendBadgeClass: trendBadgeMap[trend],
      };
    });
  }, [sameStoreComparisons, t]);

  const closeoutSummary = useMemo(
    () => ({
      cashSalesCents: zReport.cash_sales_cents ?? 0,
      cashRefundsCents: zReport.cash_refunds_cents ?? 0,
      expectedCashDepositCents: (zReport.cash_sales_cents ?? 0) - (zReport.cash_refunds_cents ?? 0),
      cardSalesCents: tenders.card_cents ?? 0,
      tipsCents: tenders.tips_cents ?? 0,
      netSalesCents: zReport.net_sales_cents ?? 0,
      grossSalesCents: zReport.gross_sales_cents ?? zReport.cash_sales_cents ?? 0,
      discountCents: zReport.discount_cents ?? 0,
      taxCents: zReport.tax_cents ?? 0,
      ordersCompleted: summary.orders ?? 0,
      refundsIssued: tenders.refunds_cents ?? 0,
    }),
    [summary.orders, tenders.card_cents, tenders.refunds_cents, tenders.tips_cents, zReport.cash_refunds_cents, zReport.cash_sales_cents, zReport.discount_cents, zReport.gross_sales_cents, zReport.net_sales_cents, zReport.tax_cents],
  );

  const refundRows = useMemo(
    () => {
      const activity = reportsQuery.data?.refund_activity ?? [];

      return activity
        .slice()
        .sort((a, b) => (b.processed_at ?? 0) - (a.processed_at ?? 0))
        .map((entry) => ({
          id: entry.payment_id,
          order: entry.order_code ?? entry.order_id ?? t('dashboard:reports.refunds.unknownOrder', { defaultValue: 'Unknown order' }),
          method: entry.method ? entry.method.toUpperCase() : t('dashboard:reports.refunds.unknownMethod', { defaultValue: 'Unknown' }),
          amount: currencyFormatter.format(entry.amount_cents / 100),
          processed: formatDistanceToNow(new Date(entry.processed_at), { addSuffix: true }),
          reason:
            entry.reason ??
            t('dashboard:reports.refunds.defaultReason', {
              defaultValue: 'No reason provided',
            }),
        }));
    },
    [reportsQuery.data?.refund_activity, t],
  );

  const lastUpdated = reportsQuery.dataUpdatedAt
    ? new Date(reportsQuery.dataUpdatedAt).toLocaleTimeString()
    : "Just now";

  const disableExport =
    reportsQuery.isLoading ||
    reportsErrorMessage !== null ||
    salesExport.isRunning ||
    itemsExport.isRunning;

  const handlePresetSave = useCallback(() => {
    const trimmed = presetName.trim();
    if (trimmed.length === 0) {
      toast({
        title: t("dashboard:reports.presets.toastRequiredTitle", { defaultValue: "Add a name" }),
        description: t("dashboard:reports.presets.toastRequiredDescription", {
          defaultValue: "Give this preset a memorable label.",
        }),
        variant: "destructive",
      });
      return;
    }
    const duplicate = savedPresets.find(
      (preset) => preset.label.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      toast({
        title: t("dashboard:reports.presets.toastDuplicateTitle", {
          defaultValue: "Preset already exists",
        }),
        description: t("dashboard:reports.presets.toastDuplicateDescription", {
          defaultValue: "Rename this preset or remove the existing one first.",
        }),
        variant: "destructive",
      });
      return;
    }
    const preset: RangePreset = { id: createRandomId(), label: trimmed, from, to };
    setSavedPresets((previous) => [...previous, preset]);
    toast({
      title: t("dashboard:reports.presets.toastSavedTitle", { defaultValue: "Preset saved" }),
      description: t("dashboard:reports.presets.toastSavedDescription", {
        defaultValue: "Apply it any time from the presets list.",
      }),
    });
    setPresetDialogOpen(false);
    setPresetName("");
  }, [from, savedPresets, presetName, t, toast, to]);

  const handlePresetApply = useCallback(
    (preset: RangePreset) => {
      setFrom(preset.from);
      setTo(preset.to);
    },
    [setFrom, setTo],
  );

  const handlePresetDelete = useCallback((presetId: string) => {
    setSavedPresets((previous) => previous.filter((entry) => entry.id !== presetId));
  }, []);

  const handleScheduleSubmit = useCallback(() => {
    const trimmedLabel = scheduleForm.label.trim();
    if (!trimmedLabel) {
      toast({
        title: t("dashboard:reports.schedules.toastLabelRequiredTitle", {
          defaultValue: "Name the schedule",
        }),
        description: t("dashboard:reports.schedules.toastLabelRequiredDescription", {
          defaultValue: "Add a label so teammates know what will be delivered.",
        }),
        variant: "destructive",
      });
      return;
    }

    const recipients = parseRecipientList(scheduleForm.recipients);
    if (recipients.length === 0) {
      toast({
        title: t("dashboard:reports.schedules.toastRecipientsRequiredTitle", {
          defaultValue: "Add recipients",
        }),
        description: t("dashboard:reports.schedules.toastRecipientsRequiredDescription", {
          defaultValue: "Provide at least one email address separated by commas.",
        }),
        variant: "destructive",
      });
      return;
    }

    const [hourCandidate, minuteCandidate] = scheduleForm.time.split(":");
    const hour = Number.parseInt(hourCandidate ?? "", 10);
    const minute = Number.parseInt(minuteCandidate ?? "", 10);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      toast({
        title: t("dashboard:reports.schedules.toastTimeInvalidTitle", {
          defaultValue: "Invalid time",
        }),
        description: t("dashboard:reports.schedules.toastTimeInvalidDescription", {
          defaultValue: "Select a valid delivery time (24-hour format).",
        }),
        variant: "destructive",
      });
      return;
    }

    const payload = {
      label: trimmedLabel,
      cadence: scheduleForm.cadence,
      dayOfWeek:
        scheduleForm.cadence === "weekly"
          ? Number.parseInt(scheduleForm.dayOfWeek, 10)
          : undefined,
      dayOfMonth:
        scheduleForm.cadence === "monthly"
          ? Number.parseInt(scheduleForm.dayOfMonth, 10)
          : undefined,
      hour,
      minute,
      recipients,
      target: scheduleForm.target,
      format: scheduleForm.format,
    };

    createScheduleMutation.mutate(payload);
  }, [createScheduleMutation, scheduleForm, t, toast]);

  const handleDeleteSchedule = useCallback(
    (scheduleId: string) => {
      deleteScheduleMutation.mutate(scheduleId);
    },
    [deleteScheduleMutation],
  );

  const handleSnapshotDownload = useCallback(async () => {
    setSnapshotDownloading(true);
    try {
      const { blob, filename } = await downloadFinanceSnapshot({
        from,
        to,
        title: snapshotTitle.trim() || undefined,
        notes: snapshotNotes.trim() || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: t("dashboard:reports.snapshots.toastSuccessTitle", {
          defaultValue: "Snapshot ready",
        }),
        description: t("dashboard:reports.snapshots.toastSuccessDescription", {
          defaultValue: "Board-ready PDF download started.",
        }),
      });
    } catch (error) {
      toast({
        title: t("dashboard:reports.snapshots.toastErrorTitle", {
          defaultValue: "Unable to generate snapshot",
        }),
        description: error instanceof Error ? error.message : t("dashboard:reports.snapshots.toastErrorDescription", {
          defaultValue: "Try again in a moment.",
        }),
        variant: "destructive",
      });
    } finally {
      setSnapshotDownloading(false);
    }
  }, [from, snapshotNotes, snapshotTitle, t, toast, to]);

  const quickRanges = [
    { label: "Today", range: 0 },
    { label: "7 Days", range: 6 },
    { label: "30 Days", range: 29 },
  ];
  const destinationLabels = useMemo(
    () => ({
      accounting: t("dashboard:reports.exports.destinationLabels.accounting", {
        defaultValue: "Accounting",
      }),
      inventory: t("dashboard:reports.exports.destinationLabels.inventory", {
        defaultValue: "Inventory system",
      }),
      warehouse: t("dashboard:reports.exports.destinationLabels.warehouse", {
        defaultValue: "Warehouse",
      }),
    }),
    [t],
  );

  const handleQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setFrom(start.toISOString().split("T")[0]);
    setTo(end.toISOString().split("T")[0]);
  };

  const handleAccountingExport = useCallback(
    async (type: AccountingExportType) => {
      if (!tenantSlug) {
        toast({
          title: "Unable to export",
          description: "Tenant context is missing.",
          variant: "destructive",
        });
        return;
      }
      try {
        const { blob, filename } = await requestAccountingExport(tenantSlug, { type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast({
          title: `${type === "accounting" ? "Accounting" : "Tax"} export ready`,
          description: "Download started.",
        });
      } catch (error) {
        toast({
          title: "Export failed",
          description: error instanceof Error ? error.message : "Unknown error occurred",
          variant: "destructive",
        });
      }
    },
    [tenantSlug, toast],
  );

  const incidentSummary = incidentQuery.data?.summary ?? null;
  const incidentMetrics = incidentSummary
    ? [
        { key: "open", label: "Open incidents", value: incidentSummary.open.toLocaleString() },
        { key: "resolved", label: "Resolved", value: incidentSummary.resolved.toLocaleString() },
        {
          key: "mtta",
          label: "MTTA (min)",
          value: incidentSummary.mttaMinutes !== null ? incidentSummary.mttaMinutes.toString() : "-",
        },
        {
          key: "mttr",
          label: "MTTR (min)",
          value: incidentSummary.mttrMinutes !== null ? incidentSummary.mttrMinutes.toString() : "-",
        },
      ]
    : [];
  const incidentSeverityBreakdown = incidentQuery.data?.bySeverity ?? [];
  const incidentTypeBreakdown = incidentQuery.data?.byType ?? [];
  const incidentItems = incidentQuery.data?.incidents ?? [];
  const incidentSeverityOptions = [
    { value: "all", label: "All severities" },
    { value: "critical", label: "Critical" },
    { value: "warning", label: "Warning" },
    { value: "info", label: "Info" },
  ];
  const severityPalette: Record<string, string> = {
    critical: "border border-rose-500/40 bg-rose-500/10 text-rose-600",
    warning: "border border-amber-500/40 bg-amber-500/10 text-amber-600",
    info: "border border-sky-500/30 bg-sky-500/10 text-sky-600",
  };
  const sortedSchedules = useMemo(
    () => schedules.slice().sort((a, b) => b.createdAt - a.createdAt),
    [schedules],
  );
  const scheduleMutationPending = createScheduleMutation.isPending;
  const deleteSchedulePending = deleteScheduleMutation.isPending;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background transition-colors duration-200" data-testid="reports-page">
      <div
        className="pointer-events-none absolute inset-0 -z-20 opacity-95"
        style={{ backgroundImage: "var(--gradient-hero)" }}
      />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(120%_140%_at_50%_-20%,rgba(59,130,246,0.25),transparent_58%)] dark:bg-[radial-gradient(130%_160%_at_50%_115%,rgba(15,118,110,0.65),rgba(2,6,23,0.85))]" />

      <div className="relative z-10 mx-auto max-w-[1600px] space-y-6 px-6 pb-8 pt-2 sm:px-8 lg:px-10">
        <section
          className={cn(
            "relative overflow-hidden rounded-3xl border border-border/60 p-6 shadow-sm",
            "bg-gradient-to-br from-sky-100 via-blue-50 to-white",
            "dark:border-border/50 dark:bg-gradient-to-br dark:from-slate-950 dark:via-indigo-950/60 dark:to-slate-950",
          )}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.18),_transparent_62%)] dark:bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.28),_transparent_58%)]" />
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-10">
            <div className="space-y-3">
              <div className="flex flex-col gap-2">
                <h1 className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-3xl font-bold tracking-tight text-transparent dark:from-white dark:via-gray-100 dark:to-gray-300">
                  Analytics Command Center
                </h1>
                <p className="max-w-xl text-muted-foreground">
                  Monitor revenue momentum, discover top performers, and export production-ready reports.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground">
                <Badge
                  variant="outline"
                  className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-emerald-700 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300"
                >
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  Data pipeline healthy
                </Badge>
                <span>Last updated {lastUpdated}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={disableExport}
                    className={cn(
                      "w-full justify-center gap-2 bg-white/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900/60 sm:min-w-[150px] sm:w-auto",
                      disableExport && "pointer-events-none opacity-60",
                    )}
                  >
                    <Download className={cn("mr-2 h-4 w-4", salesExport.isRunning && "animate-spin")} />
                    {salesExport.isRunning ? "Preparing…" : "Export Sales"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={disableExport || salesExport.isRunning} onClick={() => salesExport.startExport("csv")}>
                    Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={disableExport || salesExport.isRunning} onClick={() => salesExport.startExport("json")}>
                    Export JSON
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>
                    {t('dashboard:reports.exports.menuLabel', { defaultValue: 'Automations' })}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    disabled={disableExport || salesExport.isRunning}
                    onClick={() =>
                      salesExport.startExport(
                        "csv",
                        undefined,
                        { mode: "webhook", destination: "accounting" },
                      )
                    }
                  >
                    {t('dashboard:reports.exports.actions.sendAccounting', { defaultValue: 'Send to accounting' })}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={disableExport || salesExport.isRunning}
                    onClick={() =>
                      salesExport.startExport(
                        "csv",
                        undefined,
                        { mode: "webhook", destination: "warehouse" },
                      )
                    }
                  >
                    {t('dashboard:reports.exports.actions.sendWarehouse', { defaultValue: 'Send to warehouse' })}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={disableExport}
                    className={cn(
                      "w-full justify-center gap-2 bg-white/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-900/60 sm:min-w-[150px] sm:w-auto",
                      disableExport && "pointer-events-none opacity-60",
                    )}
                  >
                    <Download className={cn("mr-2 h-4 w-4", itemsExport.isRunning && "animate-spin")} />
                    {itemsExport.isRunning ? "Preparing…" : "Export Items"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={disableExport || itemsExport.isRunning} onClick={() => itemsExport.startExport("csv")}>
                    Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={disableExport || itemsExport.isRunning} onClick={() => itemsExport.startExport("json")}>
                    Export JSON
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>
                    {t('dashboard:reports.exports.menuLabel', { defaultValue: 'Automations' })}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    disabled={disableExport || itemsExport.isRunning}
                    onClick={() =>
                      itemsExport.startExport(
                        "csv",
                        undefined,
                        { mode: "webhook", destination: "inventory" },
                      )
                    }
                  >
                    {t('dashboard:reports.exports.actions.sendInventory', { defaultValue: 'Send to inventory system' })}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={disableExport || itemsExport.isRunning}
                    onClick={() =>
                      itemsExport.startExport(
                        "csv",
                        undefined,
                        { mode: "webhook", destination: "warehouse" },
                      )
                    }
                  >
                    {t('dashboard:reports.exports.actions.sendWarehouse', { defaultValue: 'Send to warehouse' })}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <ReportMetricCard key={metric.key} metric={metric} />
          ))}
        </section>

        <section className="grid gap-4 xl:grid-cols-[2fr_1fr]">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.customerSegments.trendTitle', { defaultValue: 'Guest engagement' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.customerSegments.trendDescription', {
                  defaultValue: 'Monitor new vs returning guests and loyalty adoption across the selected range.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {guestSummary ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4 dark:border-emerald-800/50 dark:bg-emerald-900/40">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                        {t('dashboard:reports.customerSegments.newGuests', { defaultValue: 'New guests' })}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-emerald-800 dark:text-emerald-100">
                        {quantityFormatter.format(guestSummary.totalNewGuests)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('dashboard:reports.customerSegments.newGuestsHelper', {
                          defaultValue: 'Profiles ordering for the first time',
                        })}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        {t('dashboard:reports.customerSegments.returningGuests', { defaultValue: 'Returning guests' })}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {quantityFormatter.format(guestSummary.totalReturningGuests)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('dashboard:reports.customerSegments.returningGuestsHelper', {
                          defaultValue: 'Profiles placing repeat orders',
                        })}
                      </p>
                    </div>
                    <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-4 dark:border-sky-800/50 dark:bg-sky-900/40">
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-300">
                        {t('dashboard:reports.customerSegments.repeatRate', { defaultValue: 'Repeat rate' })}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-sky-800 dark:text-sky-100">
                        {percentFormatter.format(guestSummary.repeatRate / 100)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('dashboard:reports.customerSegments.repeatRateHelper', {
                          defaultValue: 'Share of orders attributed to returning guests',
                        })}
                      </p>
                      <div className="mt-3">
                        <Progress value={Math.min(100, Math.max(0, guestSummary.repeatRate))} className="h-2" />
                      </div>
                    </div>
                  </div>
                  {guestSummary.anonymousOrders > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard:reports.customerSegments.anonymousHint', {
                        defaultValue: '{{count}} orders missing guest profiles • capture phone or email to unlock trends.',
                        count: quantityFormatter.format(guestSummary.anonymousOrders),
                      })}
                    </p>
                  ) : null}
                  {guestTrendBarData.length > 0 ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {t('dashboard:reports.customerSegments.dailyTitle', { defaultValue: 'Daily guest trend' })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.customerSegments.dailyDescription', {
                            defaultValue: 'New vs returning guests over the last seven days.',
                          })}
                        </p>
                      </div>
                      <BarList data={guestTrendBarData} toneOverride="emerald" />
                    </div>
                  ) : (
                    <div className="rounded-md border border-dashed border-muted-foreground/30 p-4 text-sm text-muted-foreground">
                      {t('dashboard:reports.customerSegments.emptyStateDescription', {
                        defaultValue: 'Capture guest contact details to start tracking engagement trends.',
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed border-muted-foreground/30 p-4 text-sm text-muted-foreground">
                  {t('dashboard:reports.customerSegments.emptyStateDescription', {
                    defaultValue: 'Capture guest contact details to start tracking engagement trends.',
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.customerSegments.loyaltyTitle', { defaultValue: 'Loyalty & marketing' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.customerSegments.loyaltyDescription', {
                  defaultValue: 'Understand loyalty participation and the channels driving revenue.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {guestSummary ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4 dark:border-emerald-800/50 dark:bg-emerald-900/40">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                      {t('dashboard:reports.customerSegments.loyaltyOrders', { defaultValue: 'Loyalty orders' })}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-emerald-800 dark:text-emerald-100">
                      {quantityFormatter.format(guestSummary.loyaltyOrders)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard:reports.customerSegments.loyaltyHelper', {
                        defaultValue: '{{orders}} loyalty orders · {{redeemed}} pts redeemed',
                        orders: quantityFormatter.format(guestSummary.loyaltyOrders),
                        redeemed: quantityFormatter.format(guestSummary.loyaltyPointsRedeemed),
                      })}
                    </p>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                        <span>
                          {t('dashboard:reports.customerSegments.loyaltyParticipation', {
                            defaultValue: 'Participation',
                          })}
                        </span>
                        <span className="text-foreground">
                          {percentFormatter.format(guestSummary.loyaltyParticipationRate / 100)}
                        </span>
                      </div>
                      <Progress
                        value={Math.min(100, Math.max(0, guestSummary.loyaltyParticipationRate))}
                        className="h-2"
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.customerSegments.loyaltyPointsAwarded', { defaultValue: 'Points awarded' })}
                      </p>
                      <p className="mt-1 font-semibold text-foreground">
                        {quantityFormatter.format(guestSummary.loyaltyPointsAwarded)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.customerSegments.loyaltyPointsRedeemed', { defaultValue: 'Points redeemed' })}
                      </p>
                      <p className="mt-1 font-semibold text-foreground">
                        {quantityFormatter.format(guestSummary.loyaltyPointsRedeemed)}
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4 text-sm dark:border-emerald-800/40 dark:bg-emerald-900/30">
                      <p className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.customerSegments.loyaltyAverage', { defaultValue: 'Avg points per loyalty order' })}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-emerald-800 dark:text-emerald-100">
                        {quantityFormatter.format(averageLoyaltyPointsPerOrder)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('dashboard:reports.customerSegments.loyaltyAverageHelper', {
                          defaultValue: 'Awarded points divided by loyalty-tagged orders.',
                        })}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">
                            {t('dashboard:reports.customerSegments.loyaltyProgramStatus', {
                              defaultValue: 'Program status',
                            })}
                          </p>
                          <p className="mt-1 font-semibold text-foreground">
                            {(loyaltyProgram?.status ?? 'disabled').replace(/_/g, ' ').replace(/\\b\\w/g, (char) => char.toUpperCase())}
                          </p>
                        </div>
                        <Badge
                          className={
                            loyaltyProgram?.status === 'enabled'
                              ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20'
                              : loyaltyProgram?.status === 'preview'
                                ? 'bg-amber-500/10 text-amber-600 ring-amber-500/20'
                                : 'bg-slate-500/10 text-slate-600 ring-slate-500/20'
                          }
                        >
                          {(loyaltyProgram?.status ?? 'disabled').toUpperCase()}
                        </Badge>
                      </div>
                      {loyaltyProgram ? (
                        <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                          <div className="flex items-center justify-between">
                            <span>{t('dashboard:reports.customerSegments.loyaltyProgramEarnMode', { defaultValue: 'Earn mode' })}</span>
                            <span className="font-semibold text-foreground">
                              {loyaltyProgram.earnMode === 'per_spend'
                                ? t('dashboard:reports.customerSegments.loyaltyEarnPerSpend', { defaultValue: 'Per spend' })
                                : loyaltyProgram.earnMode === 'per_order'
                                  ? t('dashboard:reports.customerSegments.loyaltyEarnPerOrder', { defaultValue: 'Per order' })
                                  : t('dashboard:reports.customerSegments.loyaltyEarnHybrid', { defaultValue: 'Hybrid' })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t('dashboard:reports.customerSegments.loyaltyProgramPointsPerDollar', { defaultValue: 'Points per $1 spend' })}</span>
                            <span className="font-semibold text-foreground">
                              {pointsPerDollarEarnRate ? pointsPerDollarEarnRate.toFixed(2) : '0'}
                            </span>
                          </div>
                          {loyaltyProgram.pointsPerOrder ? (
                            <div className="flex items-center justify-between">
                              <span>{t('dashboard:reports.customerSegments.loyaltyProgramPointsPerOrder', { defaultValue: 'Points per order' })}</span>
                              <span className="font-semibold text-foreground">
                                {quantityFormatter.format(loyaltyProgram.pointsPerOrder)}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <p className="mt-3 text-xs text-muted-foreground">
                          {t('dashboard:reports.customerSegments.loyaltyProgramDisabled', {
                            defaultValue: 'Your loyalty program is currently disabled.',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  {loyaltyProgramQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">{t('dashboard:reports.customerSegments.loyaltyProgramLoading', { defaultValue: 'Loading loyalty configuration…' })}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {t('dashboard:reports.customerSegments.marketingTitle', { defaultValue: 'Marketing sources' })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard:reports.customerSegments.marketingDescription', {
                      defaultValue: 'Revenue attributed to QR, counter, and connected channels.',
                    })}
                  </p>
                </div>
                {marketingBarData.length > 0 ? (
                  <BarList data={marketingBarData} toneOverride="sky" />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard:reports.customerSegments.marketingEmpty', {
                      defaultValue: 'No attribution data captured during this range.',
                    })}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.fulfillmentChannels.title', {
                  defaultValue: 'Channel fulfillment performance',
                })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.fulfillmentChannels.description', {
                  defaultValue: 'Compare on-premise vs marketplace delivery volume and SLA health.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {channelFulfillmentRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:reports.fulfillmentChannels.empty', {
                    defaultValue: 'No fulfillment channel data captured during this range.',
                  })}
                </p>
              ) : (
                channelFulfillmentRows.map((row) => (
                  <div
                    key={row.key}
                    className="rounded-lg border border-border bg-muted/20 p-4 shadow-sm transition hover:border-emerald-200/60 hover:bg-emerald-50/40 dark:hover:border-emerald-800/60 dark:hover:bg-emerald-950/20"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{row.label}</p>
                        <p className="text-xs text-muted-foreground">{row.description}</p>
                      </div>
                      <Badge variant="outline" className="w-fit text-xs font-semibold">
                        {t('dashboard:reports.fulfillmentChannels.metrics.orders', { defaultValue: 'Orders' })}: {row.ordersLabel}
                      </Badge>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-xs uppercase text-muted-foreground">
                          {t('dashboard:reports.fulfillmentChannels.metrics.revenue', { defaultValue: 'Revenue' })}
                        </p>
                        <p className="text-sm font-semibold text-foreground">{row.revenueLabel}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.fulfillmentChannels.metrics.averageOrderValue', {
                            defaultValue: 'Avg order',
                          })}{' '}
                          · {row.averageOrderLabel}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
                          <span>
                            {t('dashboard:reports.fulfillmentChannels.metrics.slaOnTime', {
                              defaultValue: 'On-time rate',
                            })}
                          </span>
                          <span className="text-foreground">{row.onTimeRateLabel}</span>
                        </div>
                        <Progress value={row.onTimeRateValue} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.fulfillmentChannels.metrics.slaBreaches', {
                            defaultValue: 'Breaches',
                          })}
                          : {row.breachesLabel} ·{" "}
                          {t('dashboard:reports.fulfillmentChannels.metrics.breachRate', {
                            defaultValue: 'Breach rate',
                          })}
                          : {row.breachRateLabel}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase text-muted-foreground">
                          {t('dashboard:reports.fulfillmentChannels.metrics.slaEvaluated', {
                            defaultValue: 'Evaluated for SLA',
                          })}
                        </p>
                        <p className="text-sm font-semibold text-foreground">{row.evaluatedLabel}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.fulfillmentChannels.metrics.slaDelay', {
                            defaultValue: 'Avg delay (breach)',
                          })}
                          : {row.delayLabel}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </section>

        {cateringOverview ? (
          <section className="grid gap-4 xl:grid-cols-[2fr_1fr]">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>
                  {t('dashboard:reports.catering.summaryTitle', { defaultValue: 'Catering pipeline' })}
                </CardTitle>
                <CardDescription>
                  {t('dashboard:reports.catering.summaryDescription', {
                    defaultValue: 'Monitor event pipeline value, conversion, and payment health for catering engagements.',
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4 dark:border-emerald-800/50 dark:bg-emerald-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                    {t('dashboard:reports.catering.metrics.pipeline', { defaultValue: 'Pipeline value' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-emerald-800 dark:text-emerald-200">
                    {cateringOverview.pipelineValue}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard:reports.catering.metrics.pipelineHelper', {
                      defaultValue: '{{count}} events in planning',
                      count: cateringOverview.totals.planning,
                    })}
                  </p>
                </div>
                <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-4 dark:border-sky-800/50 dark:bg-sky-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-300">
                    {t('dashboard:reports.catering.metrics.conversion', { defaultValue: 'Conversion rate' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-sky-800 dark:text-sky-200">
                    {cateringOverview.conversionLabel}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard:reports.catering.metrics.confirmedHelper', {
                      defaultValue: '{{count}} confirmed events',
                      count: cateringOverview.totals.confirmed,
                    })}
                  </p>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-4 dark:border-amber-800/50 dark:bg-amber-900/30">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                    {t('dashboard:reports.catering.metrics.outstanding', { defaultValue: 'Outstanding payments' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-amber-700 dark:text-amber-200">
                    {cateringOverview.outstandingValue}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('dashboard:reports.catering.metrics.paymentsHelper', {
                      defaultValue: '{{dueSoon}} due soon · {{overdue}} overdue',
                      dueSoon: cateringOverview.payments.dueSoon,
                      overdue: cateringOverview.payments.overdue,
                    })}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t('dashboard:reports.catering.metrics.confirmedValue', { defaultValue: 'Confirmed revenue' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {cateringOverview.confirmedValue}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                    {t('dashboard:reports.catering.metrics.averageGuests', { defaultValue: 'Average guests' })}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {cateringOverview.averageGuests}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>{t('dashboard:reports.catering.upcomingTitle', { defaultValue: 'Upcoming events' })}</CardTitle>
                <CardDescription>
                  {t('dashboard:reports.catering.upcomingDescription', {
                    defaultValue: 'Track near-term events and payment urgency.',
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {cateringOverview.upcoming.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard:reports.catering.upcomingEmpty', { defaultValue: 'No upcoming catering events in this range.' })}
                  </p>
                ) : (
                  cateringOverview.upcoming.map((item) => {
                    const dueBadge =
                      item.dueStatus === 'overdue'
                        ? t('dashboard:reports.catering.overdue', { defaultValue: 'Overdue' })
                        : item.dueStatus === 'dueSoon'
                          ? t('dashboard:reports.catering.dueSoon', { defaultValue: 'Due soon' })
                          : null;
                    return (
                      <div
                        key={item.id}
                        className="flex flex-col gap-1 rounded-lg border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/60 dark:bg-slate-900/40"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.name}</p>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{item.budget}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{item.dateLabel}</span>
                          {dueBadge ? (
                            <Badge
                              variant="outline"
                              className={cn(
                                'border text-[10px] font-semibold uppercase tracking-wide',
                                item.dueStatus === 'overdue'
                                  ? 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:border-rose-400/40 dark:bg-rose-400/10 dark:text-rose-100'
                                  : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-100',
                              )}
                            >
                              {dueBadge}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </section>
        ) : null}

        <section className="grid gap-4 xl:grid-cols-[2fr_1fr]">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.revenueBreakdown.title', { defaultValue: 'Revenue composition' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.revenueBreakdown.description', {
                  defaultValue: 'Compare gross vs net revenue and the adjustments that impact margin.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                {revenueBreakdown.items.map((item) => (
                  <div key={item.key} className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
                    <span className="text-sm font-semibold text-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('dashboard:reports.revenueBreakdown.netShareHeading', { defaultValue: 'Net share of gross' })}
                </p>
                <div className="flex items-center gap-3">
                  <Progress
                    value={Math.max(0, Math.min(100, Math.round(revenueBreakdown.netShare * 100)))}
                    className="h-2 flex-1"
                  />
                  <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">
                    {revenueBreakdown.netShareLabel}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('dashboard:reports.revenueBreakdown.netShareHelper', {
                    defaultValue: 'Percent of gross revenue retained after discounts and refunds.',
                  })}
                </p>
      </div>
        </CardContent>
      </Card>

      {comparisonMetrics.length > 0 && periodComparison ? (
        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <CardTitle>
                {t('dashboard:reports.periodComparison.title', { defaultValue: 'Period comparison' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.periodComparison.description', {
                  defaultValue: 'Changes versus the previous {{days}}-day window.',
                  days: salesByDay.length,
                })}
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('dashboard:reports.periodComparison.previousRange', {
                defaultValue: 'Previous range: {{from}} – {{to}}',
                from: periodComparison.previous_range.from,
                to: periodComparison.previous_range.to,
              })}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {comparisonMetrics.map((metric) => {
                const TrendIcon = trendIconMap[metric.trend];
                return (
                  <div key={metric.key} className="rounded-xl border border-border bg-muted/30 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-xl font-semibold text-foreground">{metric.currentLabel}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('dashboard:reports.periodComparison.previousValue', {
                        defaultValue: 'Prev: {{value}}',
                        value: metric.previousLabel,
                      })}
                    </p>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className={cn('flex items-center font-medium', trendStyleMap[metric.trend])}>
                        <TrendIcon className="mr-1 h-4 w-4" />
                        {metric.deltaLabel}
                      </span>
                      <span className="text-xs text-muted-foreground">{metric.percentLabel}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="card-elevated">
        <CardHeader>
          <div>
            <CardTitle>
              {t('dashboard:reports.sameStore.title', { defaultValue: 'Same-store performance' })}
            </CardTitle>
            <CardDescription>
              {t('dashboard:reports.sameStore.description', {
                defaultValue: 'Location deltas versus the previous range.',
              })}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {sameStoreRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('dashboard:reports.sameStore.empty', {
                defaultValue: 'No location data available for this comparison.',
              })}
            </p>
          ) : (
            <TableScrollContainer minWidthClass="min-w-[720px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('dashboard:reports.sameStore.columns.location', { defaultValue: 'Location' })}
                    </TableHead>
                    <TableHead>
                      {t('dashboard:reports.sameStore.columns.revenue', { defaultValue: 'Revenue' })}
                    </TableHead>
                    <TableHead>
                      {t('dashboard:reports.sameStore.columns.orders', { defaultValue: 'Orders' })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('dashboard:reports.sameStore.columns.trend', { defaultValue: 'Trend' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sameStoreRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell>
                        <div className="font-semibold text-foreground">{row.revenueCurrent}</div>
                        <p className={cn('text-xs', row.trendClass)}>
                          {row.revenueDeltaLabel} · {row.revenuePercentLabel}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold text-foreground">{row.ordersCurrent}</div>
                        <p className="text-xs text-muted-foreground">
                          {row.ordersDeltaLabel} · {row.ordersPercentLabel}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className={cn('inline-flex items-center gap-1 text-xs font-medium', row.trendBadgeClass)}>
                          <row.TrendIcon className="h-3.5 w-3.5" />
                          {row.trendLabel}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScrollContainer>
          )}
        </CardContent>
      </Card>

          <Card className="card-elevated">
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-6">
          <div>
            <CardTitle>
              {t('dashboard:reports.presets.title', { defaultValue: 'Presets & automation' })}
            </CardTitle>
            <CardDescription>
              {t('dashboard:reports.presets.description', {
                defaultValue: 'Save ranges, schedule exports, and share finance snapshots.',
              })}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPresetDialogOpen(true);
              }}
              className="w-full sm:w-auto"
            >
              <BookmarkPlus className="mr-2 h-4 w-4" />
              {t('dashboard:reports.presets.saveCta', { defaultValue: 'Save preset' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScheduleDialogOpen(true)}
              className="w-full sm:w-auto"
            >
              <CalendarClock className="mr-2 h-4 w-4" />
              {t('dashboard:reports.schedules.createCta', { defaultValue: 'Schedule export' })}
            </Button>
            <Button
              size="sm"
              className="w-full gap-2 sm:w-auto"
              onClick={handleSnapshotDownload}
              disabled={snapshotDownloading || reportsQuery.isLoading}
            >
              <FileText className={cn('h-4 w-4', snapshotDownloading && 'animate-spin')} />
              {snapshotDownloading
                ? t('dashboard:reports.snapshots.generating', { defaultValue: 'Generating…' })
                : t('dashboard:reports.snapshots.downloadCta', { defaultValue: 'Download snapshot' })}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('dashboard:reports.presets.heading', { defaultValue: 'Saved presets' })}
            </p>
            {savedPresets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('dashboard:reports.presets.empty', { defaultValue: 'No saved presets yet.' })}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {savedPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center gap-1 rounded-full border border-border bg-muted/40 px-3 py-1.5"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-2 px-2 text-sm"
                      onClick={() => handlePresetApply(preset)}
                    >
                      <CalendarRange className="h-3.5 w-3.5" />
                      {preset.label}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handlePresetDelete(preset.id)}
                      aria-label={t('dashboard:reports.presets.removeLabel', { defaultValue: 'Remove preset' })}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('dashboard:reports.schedules.heading', { defaultValue: 'Scheduled exports' })}
              </p>
              {schedulesQuery.isLoading ? (
                <div className="flex items-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('dashboard:reports.schedules.loading', { defaultValue: 'Loading schedules…' })}
                </div>
              ) : sortedSchedules.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:reports.schedules.empty', { defaultValue: 'No scheduled exports yet.' })}
                </p>
              ) : (
                <TableScrollContainer minWidthClass="min-w-[720px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t('dashboard:reports.schedules.columns.label', { defaultValue: 'Name' })}
                        </TableHead>
                        <TableHead>
                          {t('dashboard:reports.schedules.columns.cadence', { defaultValue: 'Cadence' })}
                        </TableHead>
                        <TableHead>
                          {t('dashboard:reports.schedules.columns.time', { defaultValue: 'Time' })}
                        </TableHead>
                        <TableHead>
                          {t('dashboard:reports.schedules.columns.recipients', { defaultValue: 'Recipients' })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.schedules.columns.actions', { defaultValue: 'Actions' })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSchedules.map((schedule) => {
                        const timeLabel = `${schedule.hour.toString().padStart(2, '0')}:${schedule.minute
                          .toString()
                          .padStart(2, '0')}`;
                        return (
                          <TableRow key={schedule.id}>
                            <TableCell className="font-medium">
                              <div>{schedule.label}</div>
                              <p className="text-xs text-muted-foreground">
                                {describeScheduleTarget(schedule.target)}
                              </p>
                            </TableCell>
                            <TableCell>{describeCadence(schedule)}</TableCell>
                            <TableCell>{timeLabel}</TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                              {schedule.recipients.join(', ')}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteSchedule(schedule.id)}
                                disabled={deleteSchedulePending}
                              >
                                <Trash2
                                  className={cn('mr-2 h-4 w-4', deleteSchedulePending && 'animate-spin')}
                                />
                                {t('dashboard:reports.schedules.deleteCta', { defaultValue: 'Remove' })}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableScrollContainer>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('dashboard:reports.snapshots.heading', { defaultValue: 'Snapshot details' })}
              </p>
              <div className="space-y-2">
                <Label htmlFor="snapshot-title">
                  {t('dashboard:reports.snapshots.titleLabel', { defaultValue: 'Title' })}
                </Label>
                <Input
                  id="snapshot-title"
                  placeholder={t('dashboard:reports.snapshots.titlePlaceholder', {
                    defaultValue: 'Finance Snapshot',
                  })}
                  value={snapshotTitle}
                  onChange={(event) => setSnapshotTitle(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="snapshot-notes">
                  {t('dashboard:reports.snapshots.notesLabel', { defaultValue: 'Notes' })}
                </Label>
                <Textarea
                  id="snapshot-notes"
                  rows={4}
                  placeholder={t('dashboard:reports.snapshots.notesPlaceholder', {
                    defaultValue: 'Optional context to include in the PDF.',
                  })}
                  value={snapshotNotes}
                  onChange={(event) => setSnapshotNotes(event.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('dashboard:reports.snapshots.helper', {
                  defaultValue:
                    'Snapshots capture the current range and mix insights into a board-ready PDF.',
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.waterfall.title', { defaultValue: 'Taxes, fees & discounts' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.waterfall.description', {
                  defaultValue: 'Visualize how adjustments and fees roll into your collected totals.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {waterfallSteps.some((step) => step.deltaValue !== 0) ? (
                <WaterfallChart steps={waterfallSteps} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:reports.waterfall.empty', {
                    defaultValue: 'No financial adjustments recorded for this range.',
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.dayparts.title', { defaultValue: 'Daypart performance' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.dayparts.description', {
                  defaultValue: 'Track when sales peak across breakfast, lunch, and late-night windows.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {daypartDistribution.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:reports.dayparts.empty', { defaultValue: 'No daypart data for this range yet.' })}
                </p>
              ) : (
                <BarList data={daypartDistribution} toneOverride="emerald" />
              )}
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>
                {t('dashboard:reports.channels.title', { defaultValue: 'Service channel mix' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.channels.description', {
                  defaultValue: 'Understand which channels drive revenue and order volume.',
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {channelDistribution.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('dashboard:reports.channels.empty', {
                    defaultValue: 'No channel activity captured for this reporting window.',
                  })}
                </p>
              ) : (
                <BarList data={channelDistribution} toneOverride="sky" />
              )}
            </CardContent>
          </Card>
        </section>

      <Card className="card-elevated">
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-6">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-primary" />
                Operational Analytics
              </CardTitle>
              <CardDescription>
                QR engagement, prep performance, and guest retention across the last {analytics?.range.days ?? 30} days.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAccountingExport("accounting")}
                disabled={!tenantSlug || analyticsQuery.isLoading}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Accounting CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAccountingExport("tax")}
                disabled={!tenantSlug || analyticsQuery.isLoading}
                className="w-full sm:w-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Export Tax CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {analyticsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading operational analytics…</p>
            ) : analyticsQuery.isError ? (
              <p className="text-sm text-destructive">Unable to load operational analytics.</p>
            ) : analytics ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">QR Scans</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {latestQr ? latestQr.scans.toLocaleString() : "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Orders routed: {latestQr ? latestQr.orders.toLocaleString() : "-"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Abandon Rate</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {analytics.abandonRate.abandonRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {analytics.abandonRate.abandonedOrders.toLocaleString()} of{" "}
                      {analytics.abandonRate.totalOrders.toLocaleString()} orders
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Average Order Value</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {latestAov ? currencyFormatter.format(latestAov.averageOrderValueCents / 100) : "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {latestAov ? latestAov.orders.toLocaleString() : "0"} orders on latest day
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-muted/20 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Prep SLA Breaches</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {prepSlaSummary ? `${prepSlaSummary.breachRate.toFixed(1)}%` : "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {prepSlaSummary ? prepSlaSummary.breaches.toLocaleString() : 0} of{" "}
                      {prepSlaSummary ? prepSlaSummary.evaluatedOrders.toLocaleString() : 0} evaluated tickets
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4">
                    <p className="text-xs font-semibold uppercase text-primary">Guest Retention</p>
                    <p className="text-3xl font-semibold text-primary">
                      {churnSummary ? `${churnSummary.churnRate.toFixed(1)}%` : "-"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {churnSummary
                        ? `${churnSummary.retainedCustomers.toLocaleString()} of ${churnSummary.activeCustomers.toLocaleString()} active guests reordered`
                        : "Insufficient data"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">
                      Automation Health
                    </p>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <li>
                        • Dashboard span: <span className="text-foreground">{analytics.range.days} days</span>
                      </li>
                      <li>
                        • Latest snapshot: <span className="text-foreground">{new Date(analytics.generatedAt).toLocaleString()}</span>
                      </li>
                      <li>
                        • Item heatmap coverage:{" "}
                        <span className="text-foreground">{analytics.itemHeatmap.length} menu items</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">Top Item Heatmap</h3>
                  {analytics.itemHeatmap.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Not enough order volume to build a heatmap yet.</p>
                  ) : (
                    <TableScrollContainer>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right">Peak Hour</TableHead>
                            <TableHead className="text-right">Quantity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {analytics.itemHeatmap.map((entry) => {
                          const max = Math.max(...entry.hourly);
                          const peakHour =
                            max > 0 ? entry.hourly.findIndex((value) => value === max) : null;
                          const label = peakHour !== null ? `${peakHour}:00` : "-";
                          return (
                            <TableRow key={entry.itemName}>
                              <TableCell className="font-medium">{entry.itemName}</TableCell>
                              <TableCell className="text-right text-muted-foreground">{label}</TableCell>
                              <TableCell className="text-right">{entry.totalQuantity.toLocaleString()}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      </Table>
                    </TableScrollContainer>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No analytics available yet for this tenant.</p>
            )}
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-6">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Filter className="h-5 w-5 text-primary" />
                Reporting Window
              </CardTitle>
              <CardDescription>Select the performance window you want to evaluate.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              {quickRanges.map((range) => (
                <Button key={range.label} variant="outline" size="sm" onClick={() => handleQuickRange(range.range)}>
                  {range.label}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From</label>
              <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">To</label>
              <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={() => reportsQuery.refetch()} disabled={reportsQuery.isLoading} className="w-full">
                Refresh analytics
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <CardTitle>
                {t('dashboard:reports.tenders.title', { defaultValue: 'Payment Mix' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.tenders.description', {
                  defaultValue: 'Track tender volume across the selected range.',
                })}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {tenderMetrics.map((metric) => (
                <div key={metric.key} className="rounded-xl border border-border bg-muted/30 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {metric.label}
                  </p>
                  <p className="mt-2 text-xl font-semibold text-foreground">{metric.value}</p>
                  <p className="text-xs text-muted-foreground">{metric.helper}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <CardTitle>
                {t('dashboard:reports.zSummary.title', { defaultValue: 'Daily Close Summary' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.zSummary.description', {
                  defaultValue: 'Z-report metrics captured for reconciliation and audit trails.',
                })}
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('dashboard:reports.zSummary.closingCash', {
                defaultValue: 'Closing drawer: {{amount}}',
                amount: currencyFormatter.format((zReport.closing_cash_cents ?? 0) / 100),
              })}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {zSummaryMetrics.map((metric) => (
                <div key={metric.key} className="rounded-xl border border-border bg-muted/30 p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {metric.label}
                  </p>
                  <p className="mt-2 text-xl font-semibold text-foreground">{metric.value}</p>
                  <p className="text-xs text-muted-foreground">{metric.helper}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <CardTitle>
                {t('dashboard:reports.closeout.title', { defaultValue: 'Shift Closeout' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.closeout.description', {
                  defaultValue: 'Summaries operators use to balance drawers and deposits.',
                })}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={closeoutExport.isRunning}
              onClick={() => closeoutExport.startExport('csv')}
              className="w-full gap-2 sm:w-auto"
            >
              <Download className={cn('h-4 w-4', closeoutExport.isRunning && 'animate-spin')} />
              {closeoutExport.isRunning
                ? t('dashboard:reports.closeout.exporting', { defaultValue: 'Preparing…' })
                : t('dashboard:reports.closeout.exportCta', { defaultValue: 'Export Closeout CSV' })}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.cashSales', { defaultValue: 'Cash sales' })}
                helper={t('dashboard:reports.closeout.helpers.cashSales', {
                  defaultValue: 'Cash collected at the drawer',
                })}
                value={currencyFormatter.format(closeoutSummary.cashSalesCents / 100)}
              />
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.cashRefunds', { defaultValue: 'Cash refunds' })}
                helper={t('dashboard:reports.closeout.helpers.cashRefunds', {
                  defaultValue: 'Cash returned to guests',
                })}
                value={currencyFormatter.format(closeoutSummary.cashRefundsCents / 100)}
              />
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.expectedDeposit', { defaultValue: 'Expected cash deposit' })}
                helper={t('dashboard:reports.closeout.helpers.expectedDeposit', {
                  defaultValue: 'Cash to leave in safe after refunds',
                })}
                value={currencyFormatter.format(closeoutSummary.expectedCashDepositCents / 100)}
              />
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.cardSales', { defaultValue: 'Card sales' })}
                helper={t('dashboard:reports.closeout.helpers.cardSales', {
                  defaultValue: 'Processor deposits pending settlement',
                })}
                value={currencyFormatter.format(closeoutSummary.cardSalesCents / 100)}
              />
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.tips', { defaultValue: 'Tips accrued' })}
                helper={t('dashboard:reports.closeout.helpers.tips', {
                  defaultValue: 'Tip pool to hand off to payroll',
                })}
                value={currencyFormatter.format(closeoutSummary.tipsCents / 100)}
              />
              <CloseoutStat
                label={t('dashboard:reports.closeout.labels.netSales', { defaultValue: 'Net sales' })}
                helper={t('dashboard:reports.closeout.helpers.netSales', {
                  defaultValue: 'After discounts and refunds',
                })}
                value={currencyFormatter.format(closeoutSummary.netSalesCents / 100)}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div>
              <CardTitle>
                {t('dashboard:reports.refunds.title', { defaultValue: 'Refund & Void Activity' })}
              </CardTitle>
              <CardDescription>
                {t('dashboard:reports.refunds.description', {
                  defaultValue: 'Detailed audit trail of manual refunds, voids, and adjustments.',
                })}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {refundRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
                {t('dashboard:reports.refunds.empty', { defaultValue: 'No refunds or voids recorded for this range.' })}
              </div>
            ) : (
              <TableScrollContainer minWidthClass="min-w-[720px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard:reports.refunds.columns.order', { defaultValue: 'Order' })}</TableHead>
                    <TableHead>{t('dashboard:reports.refunds.columns.method', { defaultValue: 'Method' })}</TableHead>
                    <TableHead>{t('dashboard:reports.refunds.columns.reason', { defaultValue: 'Reason' })}</TableHead>
                    <TableHead className="text-right">
                      {t('dashboard:reports.refunds.columns.amount', { defaultValue: 'Amount' })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('dashboard:reports.refunds.columns.processed', { defaultValue: 'Processed' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                  <TableBody>
                    {refundRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="font-medium">{row.order}</TableCell>
                      <TableCell>{row.method}</TableCell>
                      <TableCell className="text-muted-foreground">{row.reason}</TableCell>
                      <TableCell className="text-right text-destructive">{row.amount}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{row.processed}</TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScrollContainer>
            )}
         </CardContent>
       </Card>

        {reportsErrorMessage ? (
          <Card className="border border-destructive/30 bg-destructive/10 text-destructive">
            <CardContent className="space-y-2 p-6">
              <h3 className="text-sm font-semibold">Unable to load reports</h3>
              <p className="text-sm opacity-80">{reportsErrorMessage}</p>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border border-border bg-card shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl text-foreground">
                <ShieldAlert className="h-5 w-5 text-primary" />
                Incident Operations
              </CardTitle>
              <CardDescription>Track tamper alerts, acknowledgement pace, and resolution health.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={incidentSeverity} onValueChange={setIncidentSeverity}>
                <SelectTrigger className="w-full sm:w-auto sm:min-w-[160px]">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  {incidentSeverityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2 sm:w-auto"
                  disabled={incidentsExport.isRunning || incidentQuery.isLoading}
                >
                  <Download className={`h-4 w-4 ${incidentsExport.isRunning ? 'animate-spin' : ''}`} />
                  {incidentsExport.isRunning ? 'Preparing…' : 'Export incidents'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={incidentsExport.isRunning} onClick={() => incidentsExport.startExport("csv")}>
                    Export CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={incidentsExport.isRunning} onClick={() => incidentsExport.startExport("json")}>
                    Export JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            {incidentQuery.isLoading ? (
              <div className="flex items-center gap-3 rounded-lg border border-dashed border-muted-foreground/20 bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
                <Download className="h-4 w-4 animate-spin" /> Loading incident telemetry…
              </div>
            ) : incidentQuery.isError ? (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-6 text-sm text-rose-600">
                Unable to load incident report. Try adjusting the date range.
              </div>
            ) : incidentItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/20 bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
                No incident activity recorded for this range.
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {incidentMetrics.map((metric) => (
                    <div key={metric.key} className="rounded-xl border border-border bg-muted/30 p-4">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{metric.label}</p>
                      <p className="mt-2 text-xl font-semibold text-foreground">{metric.value}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {incidentSeverityBreakdown.map((entry) => (
                    <Badge
                      key={entry.severity}
                      variant="outline"
                      className={severityPalette[entry.severity] ?? 'border border-border text-foreground'}
                    >
                      {entry.severity.toUpperCase()}: {entry.count}
                    </Badge>
                  ))}
                  {incidentTypeBreakdown.slice(0, 4).map((entry) => (
                    <Badge key={entry.type} variant="outline" className="border-border text-muted-foreground">
                      {entry.type}: {entry.count}
                    </Badge>
                  ))}
                </div>

                <div className="space-y-3">
                  {incidentItems.slice(0, 6).map((incident) => {
                    const severityClass = severityPalette[incident.severity] ?? 'border border-border text-foreground';
                    const relative = formatDistanceToNow(new Date(incident.detectedAt), { addSuffix: true });
                    return (
                      <div
                        key={incident.id}
                        className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4"
                      >
                        <div className="space-y-1">
                          <Badge variant="outline" className={`rounded-full px-2 py-0.5 text-xs ${severityClass}`}>
                            {incident.severity.toUpperCase()}
                          </Badge>
                          <p className="text-sm font-medium text-foreground">
                            {incident.description ?? 'Tamper alert'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {incident.tableNumber ? `Table ${incident.tableNumber}` : 'Unassigned table'} • {incident.locationName ?? 'Unknown location'}
                          </p>
                        </div>
                        <div className="text-right text-xs text-muted-foreground">
                          <p>Detected {relative}</p>
                          {incident.resolvedAt && (
                            <p>Resolved {formatDistanceToNow(new Date(incident.resolvedAt), { addSuffix: true })}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="flex w-full items-center justify-start gap-2 overflow-x-auto rounded-xl bg-muted/40 p-1">
            <TabsTrigger value="overview" className="rounded-lg px-4 py-2 text-sm">
              Revenue Overview
            </TabsTrigger>
            <TabsTrigger value="items" className="rounded-lg px-4 py-2 text-sm">
              Top Items
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
                <div>
                  <CardTitle>Performance Snapshot</CardTitle>
                  <CardDescription>High-level metrics for the selected reporting window.</CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={disableExport} className="w-full sm:w-auto">
                      <Download className={`w-4 h-4 mr-2 ${salesExport.isRunning ? 'animate-spin' : ''}`} />
                      {salesExport.isRunning ? 'Preparing…' : 'Export'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={salesExport.isRunning} onClick={() => salesExport.startExport("csv")}>
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={salesExport.isRunning} onClick={() => salesExport.startExport("json")}>
                      Export JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent className="grid gap-6 md:grid-cols-3">
                <div className="rounded-2xl border border-border/60 bg-muted/30 p-5">
                  <div className="text-sm text-muted-foreground">Total Orders</div>
                  <div className="text-3xl font-semibold" data-testid="reports-summary-orders">
                    {summary.orders ?? 0}
                  </div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-muted/30 p-5">
                  <div className="text-sm text-muted-foreground">Revenue</div>
                  <div className="text-3xl font-semibold" data-testid="reports-summary-revenue">
                    {currencyFormatter.format((summary.total_revenue_cents ?? 0) / 100)}
                  </div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-muted/30 p-5">
                  <div className="text-sm text-muted-foreground">Average Order</div>
                  <div className="text-3xl font-semibold" data-testid="reports-summary-average">
                    {currencyFormatter.format((summary.avg_order_value_cents ?? 0) / 100)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
                <div>
                  <CardTitle>Sales Trend</CardTitle>
                  <CardDescription>Daily breakdown across the selected range.</CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={disableExport} className="w-full sm:w-auto">
                      <Download className={`w-4 h-4 mr-2 ${salesExport.isRunning ? 'animate-spin' : ''}`} />
                      {salesExport.isRunning ? 'Preparing…' : 'Export'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={salesExport.isRunning} onClick={() => salesExport.startExport("csv")}>
                      Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={salesExport.isRunning} onClick={() => salesExport.startExport("json")}>
                      Export JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <TableScrollContainer>
                  <Table data-testid="reports-sales-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesByDay.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                          No sales data for this period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      salesByDay.map((row) => (
                        <TableRow key={row.date}>
                          <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">{row.orders ?? 0}</TableCell>
                          <TableCell className="text-right">
                            {currencyFormatter.format((row.revenue_cents ?? 0) / 100)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  </Table>
                </TableScrollContainer>
              </CardContent>
            </Card>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card className="card-elevated">
                <CardHeader className="flex flex-col gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-primary" />
                    {t('dashboard:reports.menuIntelligence.inventory.title', {
                      defaultValue: 'Inventory health',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard:reports.menuIntelligence.inventory.description', {
                      defaultValue: 'Estimated COGS, waste, and low-stock alerts for the selected period.',
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-4">
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.metrics.cogs', {
                          defaultValue: 'Estimated COGS',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {currencyFormatter.format((inventoryInsights.estimated_cogs_cents ?? 0) / 100)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.metrics.margin', {
                          defaultValue: 'Estimated margin',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {currencyFormatter.format((inventoryInsights.estimated_margin_cents ?? 0) / 100)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.metrics.waste', {
                          defaultValue: 'Waste cost',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {currencyFormatter.format((wasteSummary.estimated_cost_cents ?? 0) / 100)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.metrics.prep', {
                          defaultValue: 'Prep added',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {currencyFormatter.format((prepActivitySummary.estimated_cost_cents ?? 0) / 100)}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.sections.lowStock', {
                          defaultValue: 'Low stock',
                        })}
                      </div>
                      {lowStockAlerts.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.empty.lowStock', {
                            defaultValue: 'No items are below their reorder point.',
                          })}
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {lowStockAlerts.map((alert) => (
                            <li key={alert.level_id} className="flex items-center justify-between rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2">
                              <div className="flex flex-col">
                                <span className="font-medium">{alert.product_name}</span>
                                {alert.location_name ? (
                                  <span className="text-xs text-muted-foreground">{alert.location_name}</span>
                                ) : null}
                                <span className="text-xs text-muted-foreground">
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.onHand', {
                                    defaultValue: 'On hand',
                                  })}{' '}
                                  {alert.on_hand}
                                  {alert.reorder_point !== null && (
                                    <span>
                                      {' '}/{' '}
                                      {t('dashboard:reports.menuIntelligence.inventory.labels.reorder', {
                                        defaultValue: 'Reorder',
                                      })}{' '}
                                      {alert.reorder_point}
                                    </span>
                                  )}
                                </span>
                              </div>
                              {alert.daily_burn_rate !== null && alert.daily_burn_rate > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.dailyBurn', {
                                    defaultValue: '{{value}}/day',
                                  })
                                    .replace('{{value}}', Math.max(alert.daily_burn_rate, 0).toFixed(1))}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.sections.depletion', {
                          defaultValue: 'Depletion risks',
                        })}
                      </div>
                      {depletionAlerts.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.empty.depletion', {
                            defaultValue: 'No rapid depletion alerts detected.',
                          })}
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {depletionAlerts.map((alert) => (
                            <li key={alert.level_id} className="flex items-center justify-between rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2">
                              <div className="flex flex-col">
                                <span className="font-medium">{alert.product_name}</span>
                                {alert.location_name ? (
                                  <span className="text-xs text-muted-foreground">{alert.location_name}</span>
                                ) : null}
                                <span className="text-xs text-muted-foreground">
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.onHand', {
                                    defaultValue: 'On hand',
                                  })}{' '}
                                  {alert.on_hand}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {alert.daily_burn_rate !== null
                                  ? t('dashboard:reports.menuIntelligence.inventory.labels.coverage', {
                                      defaultValue: '{{days}} day cover',
                                    }).replace(
                                      '{{days}}',
                                      Math.max(alert.on_hand / Math.max(alert.daily_burn_rate, 0.01), 0).toFixed(1),
                                    )
                                  : '-'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.sections.usageVariance', {
                            defaultValue: 'Usage variance',
                          })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.labels.varianceCost', {
                            defaultValue: 'Cost {{value}}',
                            value: currencyFormatter.format((totalVarianceCostCents ?? 0) / 100),
                          })}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>
                          {t('dashboard:reports.menuIntelligence.inventory.labels.varianceQuantity', {
                            defaultValue: '{{value}} units variance',
                            value: formatQuantity(totalVarianceQuantity),
                          })}
                        </span>
                      </div>
                      {usageVarianceProducts.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.empty.variance', {
                            defaultValue: 'No variance detected for this range.',
                          })}
                        </p>
                      ) : (
                        <>
                          <ul className="space-y-2 text-sm">
                          {usageVarianceProducts.map((product) => (
                            <li
                              key={`${product.product_id ?? 'product'}-${product.location_id ?? 'location'}`}
                              className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {product.product_name}
                                  {product.location_name ? (
                                    <span className="text-muted-foreground"> · {product.location_name}</span>
                                  ) : null}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {currencyFormatter.format((product.estimated_variance_cost_cents ?? 0) / 100)}
                                </span>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                <span>
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.saleQuantity', {
                                    defaultValue: 'Sold {{value}}',
                                    value: formatQuantity(product.sale_quantity ?? 0),
                                  })}
                                </span>
                                <span>
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.otherQuantity', {
                                    defaultValue: 'Other {{value}}',
                                    value: formatQuantity(product.other_quantity ?? 0),
                                  })}
                                </span>
                                <span>
                                  {t('dashboard:reports.menuIntelligence.inventory.labels.varianceQuantity', {
                                    defaultValue: '{{value}} variance',
                                    value: formatQuantity(product.variance_quantity ?? 0),
                                  })}
                                </span>
                              </div>
                            </li>
                          ))}
                          </ul>
                          {topParBreaches.length === 0 ? null : (
                            <div className="mt-3 space-y-2 text-xs">
                              <div className="text-xs uppercase text-muted-foreground">
                                {t('dashboard:reports.menuIntelligence.inventory.labels.parBreaches', {
                                  defaultValue: 'Par breaches',
                                })}
                              </div>
                              <ul className="space-y-2">
                                {topParBreaches.map((breach) => (
                                  <li
                                    key={`${breach.level_id}-${breach.updated_at ?? 'par'}`}
                                    className="rounded-lg border border-dashed border-destructive/40 bg-destructive/10 px-3 py-2"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium">
                                        {breach.product_name}
                                        {breach.location_name ? (
                                          <span className="text-muted-foreground"> · {breach.location_name}</span>
                                        ) : null}
                                      </span>
                                      <Badge
                                        variant={breach.severity === 'critical' ? 'destructive' : 'outline'}
                                        className="text-[10px] uppercase"
                                      >
                                        {breach.severity === 'critical'
                                          ? t('dashboard:reports.menuIntelligence.inventory.labels.severityCritical', {
                                              defaultValue: 'Critical',
                                            })
                                          : t('dashboard:reports.menuIntelligence.inventory.labels.severityWarning', {
                                              defaultValue: 'Warning',
                                            })}
                                      </Badge>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                      <span>
                                        {t('dashboard:reports.menuIntelligence.inventory.labels.onHand', {
                                          defaultValue: 'On hand',
                                        })}{' '}
                                        {formatQuantity(breach.on_hand ?? 0)}
                                      </span>
                                      {typeof breach.reorder_point === 'number' ? (
                                        <span>
                                          {t('dashboard:reports.menuIntelligence.inventory.labels.reorder', {
                                            defaultValue: 'Reorder',
                                          })}{' '}
                                          {formatQuantity(breach.reorder_point)}
                                        </span>
                                      ) : null}
                                      {typeof breach.deficit === 'number' && breach.deficit > 0 ? (
                                        <span>
                                          {t('dashboard:reports.menuIntelligence.inventory.labels.deficit', {
                                            defaultValue: 'Deficit {{value}}',
                                            value: formatQuantity(breach.deficit),
                                          })}
                                        </span>
                                      ) : null}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-foreground">
                        {t('dashboard:reports.menuIntelligence.inventory.sections.prepCadence', {
                          defaultValue: 'Prep cadence',
                        })}
                      </div>
                      {prepCadenceDaily.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.inventory.empty.prepCadence', {
                            defaultValue: 'No prep cadence data for this range.',
                          })}
                        </p>
                      ) : (
                        <div className="space-y-3 text-sm">
                          <div>
                            <div className="text-xs uppercase text-muted-foreground">
                              {t('dashboard:reports.menuIntelligence.inventory.labels.dailyCadence', {
                                defaultValue: 'Daily',
                              })}
                            </div>
                            <ul className="mt-2 space-y-1 text-xs">
                              {prepCadenceDaily.map((entry) => (
                                <li key={entry.date} className="flex items-center justify-between">
                                  <span>{new Date(entry.date).toLocaleDateString()}</span>
                                  <span>
                                    {t('dashboard:reports.menuIntelligence.inventory.labels.eventsCount', {
                                      defaultValue: '{{events}} events',
                                      events: entry.events.toLocaleString(),
                                    })}{' '}
                                    •{' '}
                                    {t('dashboard:reports.menuIntelligence.inventory.labels.quantityUnits', {
                                      defaultValue: '{{value}} units',
                                      value: entry.totalQuantity.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      }),
                                    })}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <div className="text-xs uppercase text-muted-foreground">
                              {t('dashboard:reports.menuIntelligence.inventory.labels.hourlyCadence', {
                                defaultValue: 'Hourly (UTC)',
                              })}
                            </div>
                            <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                              {prepCadenceHourly.map((entry) => (
                                <div
                                  key={entry.hour}
                                  className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2"
                                >
                                  <div className="font-medium">
                                    {entry.hour.toString().padStart(2, '0')}:00
                                  </div>
                                  <div className="text-muted-foreground">
                                    {t('dashboard:reports.menuIntelligence.inventory.labels.eventsCount', {
                                      defaultValue: '{{events}} events',
                                      events: entry.events.toLocaleString(),
                                    })}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {t('dashboard:reports.menuIntelligence.inventory.labels.quantityUnits', {
                                      defaultValue: '{{value}} units',
                                      value: entry.totalQuantity.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      }),
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader className="flex flex-col gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {t('dashboard:reports.menuIntelligence.labor.title', {
                      defaultValue: 'Labor snapshot',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard:reports.menuIntelligence.labor.description', {
                      defaultValue: 'Labor-to-sales ratio, hours, and gratuity distribution for the range.',
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.labor.metrics.laborPercent', {
                          defaultValue: 'Labor % of sales',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {laborInsights.labor_percent !== null
                          ? percentFormatter.format(Math.max(laborInsights.labor_percent, 0))
                          : '-'}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.labor.metrics.laborCost', {
                          defaultValue: 'Estimated labor cost',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {currencyFormatter.format((laborInsights.estimated_labor_cost_cents ?? 0) / 100)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.labor.metrics.overtimeShare', {
                          defaultValue: 'Overtime share',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {laborInsights.overtime_share !== null
                          ? percentFormatter.format(Math.max(laborInsights.overtime_share, 0))
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                      <div className="text-xs uppercase text-muted-foreground">
                        {t('dashboard:reports.menuIntelligence.labor.metrics.totalHours', {
                          defaultValue: 'Total hours',
                        })}
                      </div>
                      <div className="text-lg font-semibold">
                        {laborInsights.total_hours.toFixed(1)}
                      </div>
                      <div className="mt-2 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                        <span>
                          {t('dashboard:reports.menuIntelligence.labor.metrics.regularHours', {
                            defaultValue: 'Regular',
                          })}{' '}
                          {laborInsights.regular_hours.toFixed(1)}
                        </span>
                        <span>
                          {t('dashboard:reports.menuIntelligence.labor.metrics.overtimeHours', {
                            defaultValue: 'Overtime',
                          })}{' '}
                          {laborInsights.overtime_hours.toFixed(1)}
                        </span>
                        <span>
                          {t('dashboard:reports.menuIntelligence.labor.metrics.premiumHours', {
                            defaultValue: 'Premium',
                          })}{' '}
                          {laborInsights.premium_hours.toFixed(1)}
                        </span>
                        <span>
                          {t('dashboard:reports.menuIntelligence.labor.metrics.openExceptions', {
                            defaultValue: 'Open exceptions',
                          })}{' '}
                          {laborInsights.open_exceptions ?? 0}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-foreground">
                        {t('dashboard:reports.menuIntelligence.labor.metrics.gratuityByRole', {
                          defaultValue: 'Gratuity by role',
                        })}
                      </div>
                      {laborGratuityByRole.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.labor.empty.gratuity', {
                            defaultValue: 'No tip distribution recorded for this range.',
                          })}
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {laborGratuityByRole.map((entry) => (
                            <li key={entry.role} className="flex items-center justify-between rounded-lg border border-dashed border-border/60 bg-muted/20 px-3 py-2">
                              <span className="font-medium capitalize">{entry.role}</span>
                              <span className="text-xs text-muted-foreground">
                                {currencyFormatter.format((entry.tips_cents ?? 0) / 100)} •{' '}
                                {entry.percentage !== null ? percentFormatter.format(Math.max(entry.percentage, 0)) : '-'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="items" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ListOrdered className="h-5 w-5 text-primary" />
                    {t('dashboard:reports.menuIntelligence.top.title', {
                      defaultValue: 'Top items',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard:reports.menuIntelligence.top.description', {
                      defaultValue: 'Ranked by quantity sold in the selected period.',
                    })}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={disableExport} className="w-full sm:w-auto">
                      <Download className={`mr-2 h-4 w-4 ${itemsExport.isRunning ? 'animate-spin' : ''}`} />
                      {itemsExport.isRunning
                        ? t('dashboard:reports.menuIntelligence.top.export.preparing', {
                            defaultValue: 'Preparing…',
                          })
                        : t('dashboard:reports.menuIntelligence.top.export.label', {
                            defaultValue: 'Export',
                          })}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={itemsExport.isRunning}
                      onClick={() => itemsExport.startExport('csv')}
                    >
                      {t('dashboard:reports.menuIntelligence.top.export.csv', {
                        defaultValue: 'Export CSV',
                      })}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={itemsExport.isRunning}
                      onClick={() => itemsExport.startExport('json')}
                    >
                      {t('dashboard:reports.menuIntelligence.top.export.json', {
                        defaultValue: 'Export JSON',
                      })}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <TableScrollContainer>
                  <Table data-testid="reports-items-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t('dashboard:reports.menuIntelligence.top.columns.item', {
                            defaultValue: 'Item',
                        })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('dashboard:reports.menuIntelligence.top.columns.quantity', {
                          defaultValue: 'Quantity',
                        })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('dashboard:reports.menuIntelligence.top.columns.revenue', {
                          defaultValue: 'Revenue',
                        })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="py-8 text-center text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.top.empty', {
                            defaultValue: 'No item data for this range.',
                          })}
                        </TableCell>
                      </TableRow>
                    ) : (
                      topItems.map((item) => (
                        <TableRow key={item.item_id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell className="text-right">{item.qty}</TableCell>
                          <TableCell className="text-right">
                            {currencyFormatter.format((item.revenue_cents ?? 0) / 100)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  </Table>
                </TableScrollContainer>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader className="flex flex-col gap-2">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  {t('dashboard:reports.menuIntelligence.combo.title', {
                    defaultValue: 'Popular combos',
                  })}
                </CardTitle>
                <CardDescription>
                  {t('dashboard:reports.menuIntelligence.combo.description', {
                    defaultValue: 'Bundles of categories frequently purchased together.',
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TableScrollContainer minWidthClass="min-w-[720px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t('dashboard:reports.menuIntelligence.combo.columns.combo', {
                            defaultValue: 'Combo',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.combo.columns.orders', {
                            defaultValue: 'Orders',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.combo.columns.revenue', {
                            defaultValue: 'Revenue',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.combo.columns.margin', {
                            defaultValue: 'Margin',
                          })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {comboInsights.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.combo.empty', {
                            defaultValue: 'No combo data for this range.',
                          })}
                        </TableCell>
                      </TableRow>
                    ) : (
                      comboInsights.map((entry) => (
                        <TableRow key={entry.key}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{entry.label}</div>
                              {entry.topItems.length > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {entry.topItems.map((item) => `${item.name} ×${item.quantity}`).join(' • ')}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{entry.orders.toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            {currencyFormatter.format(entry.revenueCents / 100)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end">
                              <span>{currencyFormatter.format(entry.marginCents / 100)}</span>
                              <span className="text-xs text-muted-foreground">
                                {entry.marginRate !== null
                                  ? percentFormatter.format(Math.max(entry.marginRate, 0))
                                  : '-'}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  </Table>
                </TableScrollContainer>
              </CardContent>
            </Card>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card className="card-elevated">
                <CardHeader className="flex flex-col gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-primary" />
                    {t('dashboard:reports.menuIntelligence.category.title', {
                      defaultValue: 'Category mix',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard:reports.menuIntelligence.category.description', {
                      defaultValue: 'Track contribution margin across your top menu categories.',
                    })}
                  </CardDescription>
                  {categoryRecommendationMetadata ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="font-semibold uppercase">
                        {t('dashboard:reports.menuIntelligence.category.strategyBadge', {
                          defaultValue: 'Strategy: {{strategy}}',
                          strategy: t(
                            `dashboard:reports.menuIntelligence.category.strategy.${categoryRecommendationMetadata.strategy}`,
                            { defaultValue: categoryRecommendationMetadata.strategy },
                          ),
                        })}
                      </Badge>
                      {categoryRecommendationMetadata.variant ? (
                        <span>
                          {t('dashboard:reports.menuIntelligence.category.variantLabel', {
                            defaultValue: 'Variant: {{variant}}',
                            variant: categoryRecommendationMetadata.variant,
                          })}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </CardHeader>
                <CardContent>
                  <TableScrollContainer minWidthClass="min-w-[760px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">
                          {t('dashboard:reports.menuIntelligence.category.columns.category', {
                            defaultValue: 'Category',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.category.columns.quantity', {
                            defaultValue: 'Items',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.category.columns.revenue', {
                            defaultValue: 'Revenue',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.category.columns.share', {
                            defaultValue: 'Share',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.category.columns.margin', {
                            defaultValue: 'Margin',
                          })}
                        </TableHead>
                        {showCategoryRecommendationColumn ? (
                          <TableHead className="text-left">
                            {t('dashboard:reports.menuIntelligence.category.columns.reason', {
                              defaultValue: 'Highlight',
                            })}
                          </TableHead>
                        ) : null}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryInsights.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={showCategoryRecommendationColumn ? 6 : 5}
                            className="py-8 text-center text-muted-foreground"
                          >
                            {t('dashboard:reports.menuIntelligence.category.empty', {
                              defaultValue: 'No category data for this range.',
                            })}
                          </TableCell>
                        </TableRow>
                      ) : (
                        categoryInsights.map((entry) => (
                          <TableRow key={entry.key}>
                            <TableCell className="font-medium">
                              <div className="flex flex-col gap-1">
                                <span>{entry.name}</span>
                                {entry.recommendedRank ? (
                                  <Badge variant="secondary" className="w-fit text-xs font-semibold">
                                    {t('dashboard:reports.menuIntelligence.category.rankBadge', {
                                      defaultValue: '#{{rank}} recommended',
                                      rank: entry.recommendedRank,
                                    })}
                                  </Badge>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{entry.quantity.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              {currencyFormatter.format(entry.revenueCents / 100)}
                            </TableCell>
                            <TableCell className="text-right">
                              {entry.shareRatio !== null
                                ? percentFormatter.format(Math.max(entry.shareRatio, 0))
                                : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-col items-end">
                                <span>{currencyFormatter.format(entry.marginCents / 100)}</span>
                                <span className="text-xs text-muted-foreground">
                                  {entry.marginRate !== null
                                    ? percentFormatter.format(Math.max(entry.marginRate, 0))
                                    : '-'}
                                </span>
                              </div>
                            </TableCell>
                            {showCategoryRecommendationColumn ? (
                              <TableCell className="text-sm text-muted-foreground">
                                {entry.recommendationReason ??
                                  t('dashboard:reports.menuIntelligence.category.recommendationNone', {
                                    defaultValue: 'No highlight',
                                  })}
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  </TableScrollContainer>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader className="flex flex-col gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {t('dashboard:reports.menuIntelligence.location.title', {
                      defaultValue: 'Location margin',
                    })}
                  </CardTitle>
                  <CardDescription>
                    {t('dashboard:reports.menuIntelligence.location.description', {
                      defaultValue: 'Surface revenue and margin by venue to spot underperforming sites.',
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TableScrollContainer minWidthClass="min-w-[720px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                        <TableHead>
                          {t('dashboard:reports.menuIntelligence.location.columns.location', {
                            defaultValue: 'Location',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.location.columns.orders', {
                            defaultValue: 'Orders',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.location.columns.revenue', {
                            defaultValue: 'Revenue',
                          })}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('dashboard:reports.menuIntelligence.location.columns.margin', {
                            defaultValue: 'Margin',
                          })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locationMarginInsights.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                            {t('dashboard:reports.menuIntelligence.location.empty', {
                              defaultValue: 'No location performance recorded for this range.',
                            })}
                          </TableCell>
                        </TableRow>
                      ) : (
                        locationMarginInsights.map((entry) => (
                          <TableRow key={entry.key}>
                            <TableCell className="font-medium">{entry.name}</TableCell>
                            <TableCell className="text-right">{entry.orderCount.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              {currencyFormatter.format(entry.revenueCents / 100)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-col items-end">
                                <span>{currencyFormatter.format(entry.marginCents / 100)}</span>
                                <span className="text-xs text-muted-foreground">
                                  {entry.marginRate !== null
                                    ? percentFormatter.format(Math.max(entry.marginRate, 0))
                                    : '-'}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  </TableScrollContainer>
                </CardContent>
              </Card>
            </div>

            <Card className="card-elevated">
              <CardHeader className="flex flex-col gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-primary" />
                  {t('dashboard:reports.menuIntelligence.modifier.title', {
                    defaultValue: 'Modifier add-ons',
                  })}
                </CardTitle>
                <CardDescription>
                  {t('dashboard:reports.menuIntelligence.modifier.description', {
                    defaultValue: 'See which modifiers drive attach rate and incremental revenue.',
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TableScrollContainer minWidthClass="min-w-[720px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                      <TableHead>
                        {t('dashboard:reports.menuIntelligence.modifier.columns.modifier', {
                          defaultValue: 'Modifier',
                        })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('dashboard:reports.menuIntelligence.modifier.columns.attachRate', {
                          defaultValue: 'Attach rate',
                        })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('dashboard:reports.menuIntelligence.modifier.columns.revenue', {
                          defaultValue: 'Add-on revenue',
                        })}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('dashboard:reports.menuIntelligence.modifier.columns.margin', {
                          defaultValue: 'Margin',
                        })}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modifierInsights.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                          {t('dashboard:reports.menuIntelligence.modifier.empty', {
                            defaultValue: 'No modifier activity captured for this range.',
                          })}
                        </TableCell>
                      </TableRow>
                    ) : (
                      modifierInsights.map((entry) => (
                        <TableRow key={entry.key}>
                          <TableCell className="font-medium">{entry.name}</TableCell>
                          <TableCell className="text-right">
                            {entry.attachRate !== null
                              ? percentFormatter.format(Math.max(entry.attachRate, 0))
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {currencyFormatter.format(entry.revenueCents / 100)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end">
                              <span>{currencyFormatter.format(entry.marginCents / 100)}</span>
                              <span className="text-xs text-muted-foreground">
                                {entry.marginRate !== null
                                  ? percentFormatter.format(Math.max(entry.marginRate, 0))
                                  : '-'}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    </TableBody>
                  </Table>
                </TableScrollContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <Dialog
          open={presetDialogOpen}
          onOpenChange={(open) => {
            setPresetDialogOpen(open);
            if (!open) {
              setPresetName("");
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('dashboard:reports.presets.dialogTitle', { defaultValue: 'Save range preset' })}
              </DialogTitle>
              <DialogDescription>
                {t('dashboard:reports.presets.dialogDescription', {
                  defaultValue: 'Store the current date window so you can reuse it instantly.',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="preset-name">
                  {t('dashboard:reports.presets.nameLabel', { defaultValue: 'Preset name' })}
                </Label>
                <Input
                  id="preset-name"
                  value={presetName}
                  onChange={(event) => setPresetName(event.target.value)}
                  placeholder={t('dashboard:reports.presets.namePlaceholder', {
                    defaultValue: 'Week-to-date',
                  })}
                />
              </div>
              <div className="grid gap-2 rounded-lg border border-dashed border-muted-foreground/20 bg-muted/40 p-3 text-xs text-muted-foreground sm:grid-cols-2">
                <div>
                  <span className="font-semibold uppercase tracking-wide">
                    {t('dashboard:reports.presets.fromLabel', { defaultValue: 'From' })}
                  </span>
                  <p>{from}</p>
                </div>
                <div>
                  <span className="font-semibold uppercase tracking-wide">
                    {t('dashboard:reports.presets.toLabel', { defaultValue: 'To' })}
                  </span>
                  <p>{to}</p>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setPresetDialogOpen(false)}>
                {t('common:cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={handlePresetSave}>
                {t('dashboard:reports.presets.saveCta', { defaultValue: 'Save preset' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={scheduleDialogOpen}
          onOpenChange={(open) => {
            setScheduleDialogOpen(open);
            if (!open) {
              setScheduleForm(createEmptyScheduleForm());
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {t('dashboard:reports.schedules.dialogTitle', { defaultValue: 'Schedule export' })}
              </DialogTitle>
              <DialogDescription>
                {t('dashboard:reports.schedules.dialogDescription', {
                  defaultValue: 'Deliver reports to your inbox on a recurring cadence.',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="schedule-label">
                  {t('dashboard:reports.schedules.label', { defaultValue: 'Schedule name' })}
                </Label>
                <Input
                  id="schedule-label"
                  value={scheduleForm.label}
                  onChange={(event) =>
                    setScheduleForm((form) => ({ ...form, label: event.target.value }))
                  }
                  placeholder={t('dashboard:reports.schedules.labelPlaceholder', {
                    defaultValue: 'Daily sales digest',
                  })}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>
                    {t('dashboard:reports.schedules.cadenceLabel', { defaultValue: 'Cadence' })}
                  </Label>
                  <Select
                    value={scheduleForm.cadence}
                    onValueChange={(value) =>
                      setScheduleForm((form) => ({
                        ...form,
                        cadence: value as ScheduleFormState['cadence'],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cadenceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t('dashboard:reports.schedules.timeLabel', { defaultValue: 'Delivery time' })}
                  </Label>
                  <Input
                    type="time"
                    value={scheduleForm.time}
                    onChange={(event) =>
                      setScheduleForm((form) => ({ ...form, time: event.target.value }))
                    }
                  />
                </div>
              </div>
              {scheduleForm.cadence === "weekly" ? (
                <div className="space-y-2">
                  <Label>
                    {t('dashboard:reports.schedules.weekdayLabel', { defaultValue: 'Day of week' })}
                  </Label>
                  <Select
                    value={scheduleForm.dayOfWeek}
                    onValueChange={(value) =>
                      setScheduleForm((form) => ({ ...form, dayOfWeek: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOfWeekOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {scheduleForm.cadence === "monthly" ? (
                <div className="space-y-2">
                  <Label>
                    {t('dashboard:reports.schedules.dayOfMonthLabel', { defaultValue: 'Day of month' })}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    value={scheduleForm.dayOfMonth}
                    onChange={(event) =>
                      setScheduleForm((form) => ({ ...form, dayOfMonth: event.target.value }))
                    }
                  />
                </div>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="schedule-recipients">
                  {t('dashboard:reports.schedules.recipientsLabel', { defaultValue: 'Recipients' })}
                </Label>
                <Textarea
                  id="schedule-recipients"
                  rows={3}
                  value={scheduleForm.recipients}
                  onChange={(event) =>
                    setScheduleForm((form) => ({ ...form, recipients: event.target.value }))
                  }
                  placeholder={t('dashboard:reports.schedules.recipientsPlaceholder', {
                    defaultValue: 'owner@example.com, finance@example.com',
                  })}
                />
                <p className="text-xs text-muted-foreground">
                  {t('dashboard:reports.schedules.recipientsHelper', {
                    defaultValue: 'Separate addresses with commas or line breaks.',
                  })}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setScheduleDialogOpen(false);
                  setScheduleForm(createEmptyScheduleForm());
                }}
              >
                {t('common:cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={handleScheduleSubmit} disabled={scheduleMutationPending}>
                {scheduleMutationPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('dashboard:reports.schedules.creating', { defaultValue: 'Scheduling…' })}
                  </>
                ) : (
                  t('dashboard:reports.schedules.createConfirm', { defaultValue: 'Create schedule' })
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ReportsPageV3;

const METRIC_TONE_STYLES: Record<
  MetricTone,
  {
    gradient: string;
    cardAccent: string;
    iconBg: string;
    iconColor: string;
    bar: string;
  }
> = {
  indigo: {
    gradient: "bg-gradient-to-br from-indigo-500/20 via-transparent to-transparent",
    cardAccent: "bg-gradient-to-br from-white to-slate-50/80 dark:from-slate-950/80 dark:via-indigo-950/40 dark:to-slate-950/70",
    iconBg: "border border-indigo-500/20 bg-indigo-500/10 dark:bg-indigo-500/15",
    iconColor: "text-indigo-600 dark:text-indigo-300",
    bar: "bg-indigo-500/30 dark:bg-indigo-400/50",
  },
  sky: {
    gradient: "bg-gradient-to-br from-sky-500/20 via-transparent to-transparent",
    cardAccent: "bg-gradient-to-br from-white to-sky-50/70 dark:from-slate-950/80 dark:via-sky-950/40 dark:to-slate-950/70",
    iconBg: "border border-sky-500/20 bg-sky-500/10 dark:bg-sky-500/15",
    iconColor: "text-sky-600 dark:text-sky-300",
    bar: "bg-sky-500/30 dark:bg-sky-400/50",
  },
  emerald: {
    gradient: "bg-gradient-to-br from-emerald-500/20 via-transparent to-transparent",
    cardAccent: "bg-gradient-to-br from-white to-emerald-50/70 dark:from-slate-950/80 dark:via-emerald-950/35 dark:to-slate-950/70",
    iconBg: "border border-emerald-500/20 bg-emerald-500/10 dark:bg-emerald-500/15",
    iconColor: "text-emerald-600 dark:text-emerald-300",
    bar: "bg-emerald-500/30 dark:bg-emerald-400/50",
  },
  amber: {
    gradient: "bg-gradient-to-br from-amber-500/20 via-transparent to-transparent",
    cardAccent: "bg-gradient-to-br from-white to-amber-50/70 dark:from-slate-950/80 dark:via-amber-950/35 dark:to-slate-950/70",
    iconBg: "border border-amber-500/20 bg-amber-500/10 dark:bg-amber-500/15",
    iconColor: "text-amber-600 dark:text-amber-300",
    bar: "bg-amber-500/30 dark:bg-amber-400/50",
  },
};

const ReportMetricCard = ({ metric }: { metric: ReportMetric }) => {
  const Icon = metric.icon;
  const tone = METRIC_TONE_STYLES[metric.tone];

  return (
    <Card
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-slate-200/70 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg",
        "dark:border-slate-800/60",
        tone.cardAccent,
      )}
    >
      <div className={cn("pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100", tone.gradient)} />
      <CardContent className="relative space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{metric.label}</span>
            <div className="text-3xl font-bold text-foreground">{metric.value}</div>
            <p className="text-xs text-muted-foreground">{metric.helper}</p>
          </div>
          <span className={cn("inline-flex h-10 w-10 items-center justify-center rounded-xl backdrop-blur-sm", tone.iconBg)}>
            <Icon className={cn("h-5 w-5", tone.iconColor)} />
          </span>
        </div>
        <div className="flex h-14 items-end gap-1">
          {metric.bars.map((height, index) => (
            <span key={`${metric.key}-bar-${index}`} className={cn("flex-1 rounded-full", tone.bar)} style={{ height: `${height}%` }} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const CloseoutStat = ({
  label,
  helper,
  value,
}: {
  label: string;
  helper: string;
  value: string;
}) => (
  <div className="rounded-xl border border-border bg-muted/30 p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-2 text-xl font-semibold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{helper}</p>
  </div>
);
